import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import SolarInverterGridFlow from '../components/SolarInverterGridFlow';
import EnergyPowerFlowDiagram from '../components/EnergyPowerFlowDiagram';
import ProductionByCompanies from '../components/ProductionByCompanies';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useGaugeSimulation } from '../context/GaugeSimulationContext';
import { getBackendUrl } from "../config";
import { buildEnergyMeterDetailPath } from '../utils/energyMeterBrandDisplay';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
const BACKEND_URL = getBackendUrl();
/** PV Now (MW) ≥ këtë prag → status “online” (gjelb) dhe animacion rrjedhe aktiv. */
const PV_PRODUCTION_ACTIVE_MW = 0.01;
/** Interval: rifreskim i pjesshëm i stats nga data logger (vetëm kompanitë aktive në ballinë). */
const REAL_STATS_POLL_MS = 120000;
/** Mobile → pamja Production: sa shpesh rifreskohet PV tani për pajisjet e kompanisë. */
const MOBILE_PV_POLL_MS = 60000;

const DEFAULT_COLORS = {
  solar: '#fbbf24',
  grid: '#06b6d4',
  load: '#a855f7'
};

const formatLocalYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Ballina: kompanitë HIB (si EnergyMeterDetailChint) — diagramë 3D me PPV / Import / Load. */
const isHibDashboardCompanyName = (name) =>
  String(name || '').toUpperCase().includes('HIB');

/** Pt_W — mbaj njësoj me `CHINT_PT_W_RAW_TO_W_FACTOR` te faqja Chint (buildPowerFlowDiagramValues). */
const HIB_DASH_CHINT_PT_W_FACTOR = 100;

function flattenPayloadHibForFlow(obj, prefix = '') {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj)) {
    const key =
      (prefix ? `${prefix}_` : '') + String(k).replace(/\s/g, '_').toLowerCase();
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flattenPayloadHibForFlow(v, key));
    } else {
      out[key] = v;
      out[k] = v;
    }
  }
  return out;
}

function getChintPtWFromPayloadHibDash(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const scale = (raw) => Number(raw) * HIB_DASH_CHINT_PT_W_FACTOR;
  const cm = payload.chint_measurements;
  if (cm && typeof cm === 'object' && !Array.isArray(cm)) {
    const raw = cm.Pt_W ?? cm.pt_w ?? cm.PT_W;
    if (raw != null && raw !== '' && Number.isFinite(Number(raw))) return scale(raw);
  }
  const flat = flattenPayloadHibForFlow(payload);
  const nested = flat.chint_measurements_pt_w;
  if (nested != null && nested !== '' && Number.isFinite(Number(nested))) return scale(nested);
  const short = flat.pt_w ?? flat.Pt_W ?? flat.PT_W;
  if (short != null && short !== '' && Number.isFinite(Number(short))) return scale(short);
  return null;
}

/** Pt në kW (Chint HIB). */
function getChintPtKwFromPayloadHibDash(payload) {
  const w = getChintPtWFromPayloadHibDash(payload);
  if (w == null || !Number.isFinite(w)) return null;
  return w / 1000;
}

/** import_kw: Pt > 0 */
function getChintImportKwFromPtHibDash(payload) {
  const pt = getChintPtKwFromPayloadHibDash(payload);
  if (pt == null || !Number.isFinite(pt)) return 0;
  return pt > 0 ? pt : 0;
}

/** export_kw: Pt < 0 → |Pt| */
function getChintExportKwFromPtHibDash(payload) {
  const pt = getChintPtKwFromPayloadHibDash(payload);
  if (pt == null || !Number.isFinite(pt)) return 0;
  return pt < 0 ? Math.abs(pt) : 0;
}

function getInverterActivePowerKwHibDash(payload) {
  if (!payload || typeof payload !== 'object') return 0;
  const flat = flattenPayloadHibForFlow(payload);
  const kwKeys = ['active_power_kw', 'p_ac_kw', 'pac_kw'];
  for (const key of kwKeys) {
    const v = flat[key];
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
    const lk = String(key).toLowerCase();
    if (flat[lk] != null && flat[lk] !== '' && Number.isFinite(Number(flat[lk])))
      return Number(flat[lk]);
  }
  const pac = flat.pac ?? flat.active_power;
  if (pac != null && pac !== '' && Number.isFinite(Number(pac))) {
    const n = Number(pac);
    if (Math.abs(n) >= 10000) return n / 1000;
    return n;
  }
  return 0;
}

/** Kopje e `buildPowerFlowDiagramValues` nga faqja Chint — të njëjtat burime: inverterët + payload i EM. */
function buildPowerFlowDiagramValuesHibDash(rawPayload, inverterSumKw) {
  const flat = flattenPayloadHibForFlow(rawPayload || {});
  const num = (keys) => {
    for (const k of keys) {
      const v = flat[k];
      if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
      const low = String(k).toLowerCase();
      if (flat[low] != null && flat[low] !== '' && Number.isFinite(Number(flat[low])))
        return Number(flat[low]);
    }
    return 0;
  };
  let ppvKw = 0;
  if (inverterSumKw != null && Number.isFinite(inverterSumKw)) {
    ppvKw = inverterSumKw;
  } else {
    ppvKw = num(['ppv_kw', 'pv_power_kw', 'p_pv_kw', 'pv_power', 'ppv']);
  }
  const gridImportKw = getChintImportKwFromPtHibDash(rawPayload);
  const gridExportKw = getChintExportKwFromPtHibDash(rawPayload);
  const loadKw = ppvKw + gridImportKw - gridExportKw;
  return {
    ppvKw,
    batteryKw: num([
      'battery_discharge_kw',
      'battery_power_kw',
      'ess_discharge_kw',
      'bat_power_kw',
      'battery_kw',
    ]),
    loadKw,
    gridImportKw,
    gridExportKw,
  };
}

/** Si backend (weather_readings / Planifikim): data + ora lokale Europe/Belgrade. */
const BELGRADE_TZ = 'Europe/Belgrade';

const formatBelgradeYmd = (d = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: BELGRADE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (y && m && day) return `${y}-${m}-${day}`;
  } catch (_) {
    /* ignore */
  }
  return formatLocalYmd(d);
};

const belgradeHour = (d = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: BELGRADE_TZ,
      hour: 'numeric',
      hour12: false,
    }).formatToParts(d);
    const hv = parts.find((p) => p.type === 'hour')?.value;
    const n = parseInt(hv, 10);
    if (Number.isFinite(n)) return n;
  } catch (_) {
    /* ignore */
  }
  return d.getHours();
};

const weatherHourRowHasData = (r) =>
  r &&
  (r.ghi_wm2 != null ||
    r.temp_ambient_c != null ||
    r.temp_module_c != null ||
    r.wind_ms != null);

/** Zgjedh orën aktuale lokale (Belgrade), ose orën e fundit me të dhëna nëse aktualja është bosh. */
const pickWeatherHourlyRowForNow = (hourly) => {
  if (!Array.isArray(hourly) || hourly.length === 0) return null;
  const byH = new Map(hourly.map((x) => [x.hour_local, x]));
  const nowH = belgradeHour();
  let row = byH.get(nowH);
  if (weatherHourRowHasData(row)) return row;
  for (let h = nowH - 1; h >= 0; h--) {
    const r = byH.get(h);
    if (weatherHourRowHasData(r)) return r;
  }
  for (let h = nowH + 1; h <= 23; h++) {
    const r = byH.get(h);
    if (weatherHourRowHasData(r)) return r;
  }
  return row ?? hourly[hourly.length - 1] ?? null;
};

/** E njëjta formë si /api/weather/current që lexon Stacioni i Motit (pa ndryshuar UI). */
const mapWeatherHourlyRowToCurrentShape = (row) => {
  if (!row) return null;
  return {
    irradiance: row.ghi_wm2 ?? null,
    temp_ambient: row.temp_ambient_c ?? null,
    temp_module: row.temp_module_c ?? null,
    wind_speed: row.wind_ms ?? null,
    humidity: null,
    poa: null,
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, setAccessToken } = useAuth();
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [dataLoggers, setDataLoggers] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [energyMeters, setEnergyMeters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection states
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDataLogger, setSelectedDataLogger] = useState(null);
  const [timePeriod] = useState('day');
  const [chartColors] = useState(DEFAULT_COLORS);

  // All data loggers for capacity calculations
  const [allDataLoggers, setAllDataLoggers] = useState([]);

  // All inverters for company card counts (separate from filtered inverters)
  const [allInverters, setAllInverters] = useState([]);

  // Real stats per company (from data logger / energy meter readings)
  const [companyRealStats, setCompanyRealStats] = useState({});
  // Real stats nga energy meter sipas kompanisë (PV now/today/yesterday + CO2)
  const [companyEnergyStats, setCompanyEnergyStats] = useState({});
  /** HIB: vlera të njëjtë si EnergyMeterDetailChint `powerFlowKw` (latest-batch EM + inverter). */
  const [hibPowerFlowByCompanyId, setHibPowerFlowByCompanyId] = useState({});
  const companyEnergyFetchSpecRef = useRef({});
  const companyEnergyFetchMetaRef = useRef({});
  const companyTokenSyncRef = useRef({ companyId: null, inflight: false });

  // Mobile navigation: 'companies' | 'dataloggers' | 'production'
  const [mobileView, setMobileView] = useState('companies');
  // Mobile: dropdown kompanie (i njëjtë si te Pajisjet)
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Photo upload
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null); // {type: 'company', id: '...'}
  const fileInputRef = useRef(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  // Desktop: indeksi i faqes së karuselit (2 kompani / faqe) – vetëm manualisht me pikat
  const [companyCarouselIndex, setCompanyCarouselIndex] = useState(0);

  const allDataLoggersRef = useRef(allDataLoggers);
  const companiesRef = useRef(companies);
  const companyCarouselIndexRef = useRef(companyCarouselIndex);
  const selectedCompanyRef = useRef(selectedCompany);
  allDataLoggersRef.current = allDataLoggers;
  companiesRef.current = companies;
  companyCarouselIndexRef.current = companyCarouselIndex;
  selectedCompanyRef.current = selectedCompany;

  const getDashboardActiveCompanyIdSet = () => {
    const comps = companiesRef.current || [];
    const ids = new Set();
    const start = (companyCarouselIndexRef.current || 0) * 2;
    comps.slice(start, start + 2).forEach((c) => {
      if (c?.id != null) ids.add(String(c.id));
    });
    const sel = selectedCompanyRef.current;
    if (sel?.id != null) ids.add(String(sel.id));
    return ids;
  };

  // Mobile: 8 tema ngjyrash për grafikun 7 ditë – ndërrohet një nga një pas 3s mbajtje (0..7)
  const [mobileChartBarTheme, setMobileChartBarTheme] = useState(0);
  const chartLongPressTimerRef = useRef(null);
  const chartLongPressHandledRef = useRef(false);
  // Mobile: bar i hover/tap për tooltip prodhim për atë ditë
  const [mobileProductionHoveredBar, setMobileProductionHoveredBar] = useState(null);

  /** Për Recharts/SVG në mobile Production: përditëso kur ndërrohet dark/light (klasa në html). */
  const [chartThemeDark, setChartThemeDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setChartThemeDark(el.classList.contains('dark'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Desktop (web): temë e ndarë për çdo grafik – ngjyra default të ndryshme; mbaj 3s ndryshon vetëm atë grafik
  const [desktopChartThemesByIndex, setDesktopChartThemesByIndex] = useState([0, 1, 2]);
  const desktopChartLongPressTimerRef = useRef(null);
  const desktopChartLongPressHandledRef = useRef(false);
  // Për çdo kompani që po fetch-im: { meterId, period } – përditësojmë vetëm nëse përgjigja përputhet (nuk i heqim të dhënat kur efekti ridrejtohet)
  const chartFetchSpecRef = useRef({});
  const companyChartFetchTsRef = useRef({});

  // Desktop (web): periudha kohore e pavarur për çdo kompani – lidhet me kompaninë (më vonë me data logger të asaj kompanie)
  const [timePeriodByCompanyId, setTimePeriodByCompanyId] = useState({});
  // Stacioni i Motit – të dhëna nga /api/weather/hourly (weather_readings sipas kompanisë, si Planifikimi)
  const [weatherByCompanyId, setWeatherByCompanyId] = useState({});
  const weatherFetchMetaRef = useRef({});
  // Energy meters për çdo kompani (për grafikat e vogla – energy_delivered_mwh)
  const [energyMetersByCompanyId, setEnergyMetersByCompanyId] = useState({});
  const energyMetersByCompanyIdRef = useRef({});
  useEffect(() => {
    energyMetersByCompanyIdRef.current = energyMetersByCompanyId;
  }, [energyMetersByCompanyId]);
  // Të dhënat reale të grafikut për çdo kompani dhe periudhë (day/week/month/year)
  const [companyChartData, setCompanyChartData] = useState({});
  // Alarmet e sotme për ballinë (max 5 për lloj): datalogger, energy_meter, inverter
  const [dashboardAlarms, setDashboardAlarms] = useState({ datalogger: [], energy_meter: [], inverter: [] });
  // Mobile production chart: data ditore (today -> now) nga energy meter
  const [mobileDailyEnergyData, setMobileDailyEnergyData] = useState([]);
  const [mobileDailyEnergyLoading, setMobileDailyEnergyLoading] = useState(false);
  const [mobileProductionDate, setMobileProductionDate] = useState(() => formatLocalYmd(new Date()));
  /** Mobile / prodhim: fuqia aktuale (MW) për çdo pajisje sipas id (DL, EM, inverter). */
  const [mobileProductionPvNowByDeviceId, setMobileProductionPvNowByDeviceId] = useState({});

  const { capacityMW: productionGaugeCapacityMW, producingMW: productionGaugeProducingMW, getCompanyGauge } = useGaugeSimulation();

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const formatAlarmTs = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      const date = d.toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit' });
      const time = d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      return `${date} ${time}`;
    } catch (_) {
      return String(ts).slice(0, 19) || '—';
    }
  };
  const alarmPayloadLabel = (payload, fullText = false) => {
    const maxLen = fullText ? 500 : 50;
    if (!payload || typeof payload !== 'object') return '—';
    const msg = payload.message ?? payload.name ?? payload.alarm_name ?? payload.text ?? payload.description;
    if (msg != null && typeof msg === 'string' && msg.trim()) return msg.trim().slice(0, maxLen);
    if (msg != null && typeof msg === 'number') return String(msg);
    const oneVal = (v) => {
      if (v == null) return '';
      if (typeof v === 'string') return v.slice(0, maxLen);
      if (typeof v === 'number') return String(v);
      if (Array.isArray(v)) return v.map((it) => (it && typeof it === 'object' ? (it.message ?? it.name ?? it.code ?? it.text ?? JSON.stringify(it).slice(0, 80)) : String(it))).join(', ').slice(0, maxLen);
      if (typeof v === 'object') return v.message ?? v.name ?? v.code ?? v.text ?? JSON.stringify(v).slice(0, maxLen);
      return String(v).slice(0, maxLen);
    };
    const first = Object.keys(payload)[0];
    if (!first) return 'Alarm';
    const val = payload[first];
    const str = oneVal(val);
    return str ? `${first}: ${str}` : first;
  };

  useEffect(() => {
    fetchCompanies();
    fetchAllDataLoggers();
    fetchAllInverters(); // Fetch all inverters for company cards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Energy meters vetëm për kompanitë në karusel + të zgjedhurën — jo N kërkesa për çdo kompani.
  useEffect(() => {
    if (!companies?.length || !getToken()) return;
    const ids = new Set();
    const start = companyCarouselIndex * 2;
    companies.slice(start, start + 2).forEach((c) => {
      if (c?.id != null) ids.add(String(c.id));
    });
    if (selectedCompany?.id != null) ids.add(String(selectedCompany.id));
    fetchEnergyMetersForCompanyIdsIfNeeded(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, companyCarouselIndex, selectedCompany?.id]);

  const fetchDashboardAlarms = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dashboard/alarms-latest`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = res.data || {};
      setDashboardAlarms({
        datalogger: Array.isArray(data.datalogger) ? data.datalogger : [],
        energy_meter: Array.isArray(data.energy_meter) ? data.energy_meter : [],
        inverter: Array.isArray(data.inverter) ? data.inverter : [],
      });
    } catch (_) {
      setDashboardAlarms({ datalogger: [], energy_meter: [], inverter: [] });
    }
  };

  useEffect(() => {
    if (!loading && getToken()) fetchDashboardAlarms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (selectedCompany) {
      fetchDataLoggersForCompany(selectedCompany.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fetch when selectedCompany changes
  }, [selectedCompany]);

  // Kur ndryshon kompania e shfaqur (mobile/ballinë): ngarko energy meter për atë kompani në parapavij – id e kompanis → gjen metrin → grafiku 7 ditë etj.
  useEffect(() => {
    const displayedCompany = selectedCompany || companies[0];
    if (!displayedCompany?.id) return;
    const cid = String(displayedCompany.id);
    if (energyMetersByCompanyIdRef.current[cid]?.length > 0) return;
    fetchEnergyMetersForDisplayedCompany(displayedCompany.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany?.id, companies[0]?.id]);

  useEffect(() => {
    if (selectedDataLogger) {
      fetchInvertersForDataLogger(selectedDataLogger.id);
      fetchEnergyMetersForDataLogger(selectedDataLogger.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fetch when selectedDataLogger changes
  }, [selectedDataLogger]);

  // Desktop: mbaj kompaninë e zgjedhur stabile (karuseli vetëm me butonat / pikat, pa auto-slide)
  useEffect(() => {
    const isMobileNow = window.innerWidth < 768;
    if (isMobileNow) return;
    if (companies.length === 0) return;
    if (selectedCompany?.id) return;
    setSelectedCompany(companies[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, selectedCompany?.id]);

  const getToken = () => localStorage.getItem('access_token');


  const selectCompanyToken = async (companyId) => {
    const cid = companyId != null ? String(companyId) : '';
    if (!cid) return;
    const sync = companyTokenSyncRef.current;
    if (sync.inflight) return;
    if (sync.companyId === cid) return;
    companyTokenSyncRef.current = { ...sync, inflight: true };
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/select-company`,
        { company_id: companyId },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data?.access_token) {
        const incomingToken = String(response.data.access_token);
        if (incomingToken && incomingToken !== getToken()) {
          setAccessToken(incomingToken);
        }
        companyTokenSyncRef.current = { companyId: cid, inflight: false };
      } else {
        companyTokenSyncRef.current = { ...companyTokenSyncRef.current, inflight: false };
      }
    } catch (error) {
      console.error('Error selecting company:', error);
      companyTokenSyncRef.current = { ...companyTokenSyncRef.current, inflight: false };
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setCompanies(list);
      if (list.length > 0) {
        setSelectedCompany(list[0]);
        await selectCompanyToken(list[0].id);
      } else {
        setSelectedCompany(null);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDataLoggers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/data-loggers`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDataLoggers(response.data);
      setAllDataLoggers(response.data); // Keep a copy of all data loggers
    } catch (error) {
      console.error('Error fetching data loggers:', error);
    }
  };

  const fetchAllInverters = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/inverters`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setAllInverters(response.data); // Store ALL inverters for company cards
      setInverters(response.data);
    } catch (error) {
      console.error('Error fetching all inverters:', error);
    }
  };

  /** Ngarko lista energy meter vetëm për kompani që nuk janë në cache (undefined = ende pa provuar). */
  const fetchEnergyMetersForCompanyIdsIfNeeded = async (companyIdSet) => {
    if (!companyIdSet || companyIdSet.size === 0 || !getToken()) return;
    const headers = { Authorization: `Bearer ${getToken()}` };
    const toFetch = [...companyIdSet].filter((cid) => energyMetersByCompanyIdRef.current[cid] === undefined);
    if (toFetch.length === 0) return;
    const entries = await Promise.all(
      toFetch.map(async (cid) => {
        try {
          const url = `${BACKEND_URL}/api/devices/energy-meters?company_id=${encodeURIComponent(cid)}`;
          const res = await axios.get(url, { headers });
          return [cid, Array.isArray(res.data) ? res.data : []];
        } catch (_) {
          return [cid, []];
        }
      })
    );
    setEnergyMetersByCompanyId((prev) => {
      const next = { ...prev };
      entries.forEach(([cid, list]) => {
        next[cid] = list;
      });
      return next;
    });
  };

  // Për ballinë mobile: kompania e shfaqur (p.sh. FRIGOFOOD) – ngarko energy meter për atë kompani në parapavij (id e kompanis → gjen energy metrin → të dhënat).
  const fetchEnergyMetersForDisplayedCompany = async (companyId) => {
    if (!companyId || !getToken()) return;
    const cid = String(companyId);
    try {
      const url = `${BACKEND_URL}/api/devices/energy-meters?company_id=${encodeURIComponent(cid)}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      const list = Array.isArray(res.data) ? res.data : [];
      setEnergyMetersByCompanyId((prev) => ({ ...prev, [cid]: list }));
    } catch (_) {
      setEnergyMetersByCompanyId((prev) => ({ ...prev, [cid]: [] }));
    }
  };

  const fetchDataLoggersForCompany = async (companyId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/devices/data-loggers?company_id=${encodeURIComponent(companyId)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const filtered = Array.isArray(response.data) ? response.data : [];
      setDataLoggers(filtered);
      if (filtered.length > 0) {
        setSelectedDataLogger(filtered[0]);
      }
    } catch (error) {
      console.error('Error fetching data loggers:', error);
    }
  };

  const fetchInvertersForDataLogger = async (dataLoggerId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/inverters?data_logger_id=${dataLoggerId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setInverters(response.data);
    } catch (error) {
      console.error('Error fetching inverters:', error);
    }
  };

  const fetchEnergyMetersForDataLogger = async (dataLoggerId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/devices/energy-meters?data_logger_id=${encodeURIComponent(dataLoggerId)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const filtered = Array.isArray(response.data) ? response.data : [];
      setEnergyMeters(filtered);
    } catch (error) {
      console.error('Error fetching energy meters:', error);
    }
  };

  // Fetch real stats (PV Tani, PV Sot, etj.) nga pi-datalogger-readings.
  // Pa opsion: të gjithë DL-at → zëvendëson gjithë state (për totalet desktop + hidratim fillestar).
  // Me merge + restrictToCompanyIds: vetëm DL-at e atyre kompanive → bashkon në state (polling i lehtë).
  const fetchCompanyRealStats = async (options = {}) => {
    const { merge = false, restrictToCompanyIds = null } = options;
    const loggers = allDataLoggersRef.current || [];
    if (!loggers.length) return;
    const idSet = restrictToCompanyIds instanceof Set && restrictToCompanyIds.size > 0 ? restrictToCompanyIds : null;
    const toProcess = idSet
      ? loggers.filter((dl) => dl.company_id != null && idSet.has(String(dl.company_id)))
      : loggers;
    if (idSet && toProcess.length === 0) return;

    const headers = { Authorization: `Bearer ${getToken()}` };
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

    const dlIds = toProcess.map((dl) => dl.id).filter(Boolean);
    if (!dlIds.length) return;

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/dashboard/datalogger-company-real-stats-batch`,
        { data_logger_ids: dlIds, yesterday_date: yesterdayStr },
        { headers }
      );
      const statsByCompany = res.data?.stats_by_company || {};
      if (merge) {
        setCompanyRealStats((prev) => ({ ...prev, ...statsByCompany }));
      } else {
        setCompanyRealStats(statsByCompany);
      }
    } catch (err) {
      console.error('fetchCompanyRealStats', err);
    }
  };

  // Fetch PV/energy stats vetëm nga energy meter për një kompani (thirrje me company_id)
  // Me TTL + dedupe që të shmangim thirrjet e tepërta kur ndërrohet faqja e karuselit me dorë.
  const fetchCompanyEnergyStats = async (companyId, options = {}) => {
    const { force = false } = options;
    if (!companyId || !getToken()) return;
    const cid = String(companyId);
    const nowMs = Date.now();
    const ENERGY_STATS_TTL_MS = 90 * 1000;
    const meta = companyEnergyFetchMetaRef.current[cid] || {};
    if (!force && meta.inflight) return;
    if (!force && meta.lastSuccessTs && nowMs - meta.lastSuccessTs < ENERGY_STATS_TTL_MS) return;
    companyEnergyFetchMetaRef.current[cid] = { ...meta, inflight: true, lastAttemptTs: nowMs };
    const headers = { Authorization: `Bearer ${getToken()}` };
    const requestTag = `${Date.now()}-${Math.random()}`;
    companyEnergyFetchSpecRef.current[cid] = requestTag;

    const now = new Date();
    const todayStr = formatLocalYmd(now);
    const nowTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    const todayFrom = `${todayStr}T00:00:00`;
    const todayTo = `${todayStr}T${nowTime}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = formatLocalYmd(yesterday);
    const yesterdayFrom = `${yesterdayStr}T00:00:00`;
    const yesterdayTo = `${yesterdayStr}T23:59:59`;

    try {
      const qs = new URLSearchParams();
      qs.set('today_from', todayFrom);
      qs.set('today_to', todayTo);
      qs.set('yesterday_from', yesterdayFrom);
      qs.set('yesterday_to', yesterdayTo);
      const res = await axios.get(
        `${BACKEND_URL}/api/companies/${encodeURIComponent(cid)}/dashboard-energy-meter-stats?${qs.toString()}`,
        { headers }
      );
      const d = res.data || {};

      if (companyEnergyFetchSpecRef.current[cid] !== requestTag) return;

      const noMeters =
        d.pv_now_mw == null &&
        d.pv_today_mwh == null &&
        d.pv_yesterday_mwh == null &&
        d.co2_today_kg == null &&
        d.last_updated_ts == null;

      if (noMeters) {
        setCompanyEnergyStats((prev) => ({
          ...prev,
          [cid]: {
            pvNowMw: null,
            pvTodayMWh: null,
            pvYesterdayMWh: null,
            co2TodayKg: null,
            lastUpdatedTs: null,
            meterImportKw: null,
            meterExportKw: null,
          },
        }));
      } else {
        setCompanyEnergyStats((prev) => ({
          ...prev,
          [cid]: {
            pvNowMw: d.pv_now_mw,
            pvTodayMWh: d.pv_today_mwh,
            pvYesterdayMWh: d.pv_yesterday_mwh,
            co2TodayKg: d.co2_today_kg,
            lastUpdatedTs: d.last_updated_ts,
            meterImportKw: d.meter_import_kw != null ? Number(d.meter_import_kw) : null,
            meterExportKw: d.meter_export_kw != null ? Number(d.meter_export_kw) : null,
          },
        }));
      }
      companyEnergyFetchMetaRef.current[cid] = {
        ...(companyEnergyFetchMetaRef.current[cid] || {}),
        inflight: false,
        lastSuccessTs: Date.now(),
      };
    } catch (error) {
      console.error('fetchCompanyEnergyStats', cid, error);
      if (companyEnergyFetchSpecRef.current[cid] !== requestTag) return;
      setCompanyEnergyStats((prev) => ({
        ...prev,
        [cid]: {
          pvNowMw: null,
          pvTodayMWh: null,
          pvYesterdayMWh: null,
          co2TodayKg: null,
          lastUpdatedTs: null,
          meterImportKw: null,
          meterExportKw: null,
        },
      }));
      companyEnergyFetchMetaRef.current[cid] = {
        ...(companyEnergyFetchMetaRef.current[cid] || {}),
        inflight: false,
      };
    }
  };

  // HIB vetëm: diagrami 3D — të njëjtat të dhëna si faqja Chint (inverter sum + leximi më i fundit i EM).
  useEffect(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token || !companies?.length || !allDataLoggers?.length) return;

    const idSet = new Set();
    const start = companyCarouselIndex * 2;
    companies.slice(start, start + 2).forEach((c) => {
      if (c?.id != null) idSet.add(String(c.id));
    });
    if (selectedCompany?.id != null) idSet.add(String(selectedCompany.id));
    // Fleet gauges (ProductionByCompanies): Ppv për të gjitha HIB, jo vetëm karuselin
    companies.forEach((c) => {
      if (c?.id != null && isHibDashboardCompanyName(c.name)) idSet.add(String(c.id));
    });

    const hibTargets = companies.filter(
      (c) => c?.id != null && idSet.has(String(c.id)) && isHibDashboardCompanyName(c.name),
    );
    if (hibTargets.length === 0) return;

    let cancelled = false;

    const run = async () => {
      const byCompany = {};
      const allEm = [];
      const allInv = [];
      const emIdsByCid = new Map();
      const invIdsByCid = new Map();

      for (const c of hibTargets) {
        const cid = String(c.id);
        const emsKnown = Object.prototype.hasOwnProperty.call(energyMetersByCompanyId, cid);
        const emsList = emsKnown ? (energyMetersByCompanyId[cid] || []) : [];
        const dlIds = allDataLoggers
          .filter((dl) => String(dl.company_id) === cid)
          .map((dl) => dl.id)
          .filter(Boolean);
        const invs = allInverters.filter((inv) => dlIds.includes(inv.data_logger_id));
        const emIds = emsList.map((e) => e.id).filter(Boolean);
        const invIds = invs.map((i) => i.id).filter(Boolean);
        emIdsByCid.set(cid, emIds);
        invIdsByCid.set(cid, invIds);
        emIds.forEach((id) => allEm.push(id));
        invIds.forEach((id) => allInv.push(id));
      }

      const uniqEm = [...new Set(allEm)];
      const uniqInv = [...new Set(allInv)];
      if (uniqEm.length === 0 && uniqInv.length === 0) return;

      try {
        const res = await axios.post(
          `${BACKEND_URL}/api/devices/pi-readings/latest-batch`,
          {
            datalogger_ids: [],
            energy_meter_ids: uniqEm,
            inverter_ids: uniqInv,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const byId = res.data?.by_device_id || {};

        for (const c of hibTargets) {
          const cid = String(c.id);
          const emIds = emIdsByCid.get(cid) || [];
          const invIds = invIdsByCid.get(cid) || [];

          let bestPayload = null;
          let bestTs = -Infinity;
          for (const emId of emIds) {
            const readings = byId[emId]?.readings || [];
            const r = readings[0];
            if (!r?.ts) continue;
            const t = new Date(r.ts).getTime();
            if (Number.isFinite(t) && t > bestTs) {
              bestTs = t;
              bestPayload = r.payload ?? null;
            }
          }

          let plantKw = null;
          if (invIds.length > 0) {
            let sum = 0;
            let any = false;
            for (const iid of invIds) {
              const readings = byId[iid]?.readings || [];
              const p = readings[0]?.payload;
              if (!p || typeof p !== 'object') continue;
              sum += getInverterActivePowerKwHibDash(p);
              any = true;
            }
            plantKw = any ? sum : null;
          }

          byCompany[cid] = buildPowerFlowDiagramValuesHibDash(bestPayload, plantKw);
        }

        if (!cancelled && Object.keys(byCompany).length > 0) {
          setHibPowerFlowByCompanyId((prev) => ({ ...prev, ...byCompany }));
        }
      } catch (_) {
        /* ignore */
      }
    };

    run();
    const tid = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      run();
    }, REAL_STATS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(tid);
    };
  }, [
    companies,
    companyCarouselIndex,
    selectedCompany?.id,
    allDataLoggers,
    allInverters,
    energyMetersByCompanyId,
  ]);

  useEffect(() => {
    if (allDataLoggers?.length > 0) {
      fetchCompanyRealStats({ merge: false });
      const id = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;
        const activeIds = getDashboardActiveCompanyIdSet();
        if (activeIds.size === 0) return;
        fetchCompanyRealStats({ merge: true, restrictToCompanyIds: activeIds });
      }, REAL_STATS_POLL_MS);
      return () => clearInterval(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDataLoggers?.length]);

  // Kur ndërron kompania (ose faqja e carousel), rifresko stats vetëm për kompanitë e dukshme.
  useEffect(() => {
    if (!companies?.length) return;
    const ids = new Set();
    const start = companyCarouselIndex * 2;
    companies.slice(start, start + 2).forEach((c) => c?.id && ids.add(String(c.id)));
    if (selectedCompany?.id) ids.add(String(selectedCompany.id));
    ids.forEach((id) => fetchCompanyEnergyStats(id, { force: true }));
    fetchCompanyRealStats({ merge: true, restrictToCompanyIds: ids });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, companyCarouselIndex, selectedCompany?.id]);

  // Polling periodik për kompanitë aktive në ballinë.
  useEffect(() => {
    if (!companies?.length) return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      const ids = new Set();
      const start = companyCarouselIndex * 2;
      companies.slice(start, start + 2).forEach((c) => c?.id && ids.add(String(c.id)));
      if (selectedCompany?.id) ids.add(String(selectedCompany.id));
      ids.forEach((cid) => fetchCompanyEnergyStats(cid));
    }, REAL_STATS_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, companyCarouselIndex, selectedCompany?.id]);

  const fetchWeatherForCompany = async (companyId, options = {}) => {
    const { force = false } = options;
    if (!companyId) return;
    const cid = String(companyId);
    const WEATHER_TTL_MS = 5 * 60 * 1000;
    const nowMs = Date.now();
    const meta = weatherFetchMetaRef.current[cid] || {};
    if (!force && meta.inflight) return;
    if (!force && meta.lastSuccessTs && nowMs - meta.lastSuccessTs < WEATHER_TTL_MS) return;
    weatherFetchMetaRef.current[cid] = { ...meta, inflight: true, lastAttemptTs: nowMs };
    try {
      const dateStr = formatBelgradeYmd();
      const res = await axios.get(
        `${BACKEND_URL}/api/weather/hourly?company_id=${encodeURIComponent(cid)}&date=${encodeURIComponent(dateStr)}&start_hour=0&end_hour=23`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const slot = pickWeatherHourlyRowForNow(res.data?.hourly);
      setWeatherByCompanyId((prev) => ({
        ...prev,
        [cid]: mapWeatherHourlyRowToCurrentShape(slot),
      }));
      weatherFetchMetaRef.current[cid] = {
        ...(weatherFetchMetaRef.current[cid] || {}),
        inflight: false,
        lastSuccessTs: Date.now(),
      };
    } catch {
      setWeatherByCompanyId((prev) => ({ ...prev, [cid]: null }));
      weatherFetchMetaRef.current[cid] = {
        ...(weatherFetchMetaRef.current[cid] || {}),
        inflight: false,
      };
    }
  };

  useEffect(() => {
    const start = companyCarouselIndex * 2;
    const visible = companies.slice(start, start + 2);
    if (typeof document !== 'undefined' && document.hidden) return;
    visible.forEach((c) => c?.id && fetchWeatherForCompany(c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- weather for visible carousel companies only
  }, [companyCarouselIndex, companies]);

  // Always format as MW
  const formatCapacity = (kw) => {
    const n = Number(kw);
    if (!Number.isFinite(n)) return '—';
    return (n / 1000).toFixed(1);
  };

  // Format power - always MW if >= 100
  const formatPower = (kw) => {
    const n = Number(kw);
    if (!Number.isFinite(n)) {
      return { value: '—', unit: '' };
    }
    if (n >= 100) {
      return { value: (n / 1000).toFixed(2), unit: 'MW' };
    }
    return { value: n.toFixed(1), unit: 'kW' };
  };

  /** Shfaqje UI: fuqia si vlerë pozitive (pa minus në GRID / PV Now). */
  const formatAbsMw = (mw) => {
    const n = Number(mw);
    if (!Number.isFinite(n)) return null;
    return Math.abs(n).toFixed(2);
  };

  // Format energy - always MWh if >= 1000
  const formatEnergy = (kwh) => {
    const n = Number(kwh);
    if (!Number.isFinite(n)) {
      return { value: '—', unit: '' };
    }
    if (n >= 1000) {
      return { value: (n / 1000).toFixed(1), unit: 'MWh' };
    }
    return { value: n.toFixed(0), unit: 'kWh' };
  };

  const formatTimeAgo = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
    return `${Math.floor(sec / 86400)} days ago`;
  };

  // Triple click handler for photo upload
  const handlePhotoClick = (type, id) => {
    if (!isSuperAdmin) return;

    clickCountRef.current += 1;

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      // Use 'company-photo' for dashboard photos (saves to photo_url)
      const uploadType = type === 'company' ? 'company-photo' : type;
      setPhotoTarget({ type: uploadType, id });
      setShowPhotoModal(true);
    } else {
      clickTimerRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 500);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !photoTarget) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${BACKEND_URL}/api/upload/photo/${photoTarget.type}/${photoTarget.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Refresh data
      if (photoTarget.type === 'company-photo' || photoTarget.type === 'company') {
        fetchCompanies();
      } else {
        fetchAllDataLoggers();
      }
      setShowPhotoModal(false);
      toast.success('Foto u ngarkua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë ngarkimit');
    } finally {
      setUploading(false);
    }
  };

  // Get company's total capacity: sum of inverter rated_power_kw (fallback: installed_capacity_kw nga data logger)
  const getCompanyCapacity = (companyId) => {
    const companyDLs = allDataLoggers.filter(dl => dl.company_id === companyId);
    const dlIds = companyDLs.map(dl => dl.id);
    const invSum = allInverters
      .filter(inv => dlIds.includes(inv.data_logger_id))
      .reduce((s, inv) => s + (Number(inv.rated_power_kw) || Number(inv.power_kw) || 0), 0);
    if (invSum > 0) return invSum;
    const dlSum = companyDLs.reduce((sum, dl) => sum + (Number(dl.installed_capacity_kw) || 0), 0);
    return Number.isFinite(Number(dlSum)) ? Number(dlSum) : 0;
  };

  // Get company's data logger count
  const getCompanyDataLoggerCount = (companyId) => {
    return allDataLoggers.filter(dl => dl.company_id === companyId).length;
  };

  const getPhotoUrl = (item, type) => {
    // For companies in dashboard, use only photo_url (not logo_url)
    if (type === 'company') {
      if (item?.photo_url) {
        return `${BACKEND_URL}${item.photo_url}`;
      }
      // Default photo for dashboard if no photo_url uploaded
      return 'https://customer-assets.emergentagent.com/job_muj-pytja-start/artifacts/8a2lsskt_IMG-20251218-WA0002.jpg';
    }
    // For other devices (data-loggers, etc.)
    if (item?.photo_url) {
      return `${BACKEND_URL}${item.photo_url}`;
    }
    return 'https://customer-assets.emergentagent.com/job_muj-pytja-start/artifacts/8a2lsskt_IMG-20251218-WA0002.jpg';
  };

  // Nxjerr energy_delivered_mwh nga payload (energy meter) – për grafikat e vogla
  const getExportFromPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return 0;
    const flat = (o, prefix = '') => {
      const out = {};
      if (!o || typeof o !== 'object') return out;
      for (const [k, v] of Object.entries(o)) {
        const key = (prefix ? prefix + '_' : '') + String(k).replace(/\s/g, '_').toLowerCase();
        if (v != null && typeof v === 'object' && !Array.isArray(v)) Object.assign(out, flat(v, key));
        else { out[key] = v; out[k] = v; }
      }
      return out;
    };
    const f = flat(payload);
    const keys = ['energy_delivered_mwh', 'energy_delivered', 'total_export', 'exported_energy', 'export_total', 'e_export'];
    for (const k of keys) {
      const v = f[k];
      if (v != null && v !== '') {
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
      }
    }
    return 0;
  };

  // Nxjerr active/real power në MW nga payload i energy meter (|MW| për PV në UI)
  const getPowerMwFromPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return 0;
    const flat = (o, prefix = '') => {
      const out = {};
      if (!o || typeof o !== 'object') return out;
      for (const [k, v] of Object.entries(o)) {
        const key = (prefix ? prefix + '_' : '') + String(k).replace(/\s/g, '_').toLowerCase();
        if (v != null && typeof v === 'object' && !Array.isArray(v)) Object.assign(out, flat(v, key));
        else { out[key] = v; out[k] = v; }
      }
      return out;
    };
    const f = flat(payload);
    const mwKeys = ['real_power_mw', 'active_power_mw', 'total_real_power_mw', 'p_mw', 'power_mw'];
    for (const k of mwKeys) {
      const v = Number(f[k]);
      if (Number.isFinite(v)) return Math.abs(v);
    }
    const kwKeys = ['real_power_kw', 'active_power_kw', 'total_real_power_kw', 'p_kw', 'power_kw', 'active_power'];
    for (const k of kwKeys) {
      const v = Number(f[k]);
      if (Number.isFinite(v)) return Math.abs(v / 1000);
    }
    // Njëjtë si EnergyMeterDetail (total_real_power + zinxhiri); vlera >= 100 trajtohet si kW → MW
    const standardMwKeys = ['total_real_power', 'real_power', 'pac_total', 'ptot', 'active_power', 'power', 'total_active_power', 'pac'];
    for (const k of standardMwKeys) {
      const raw = f[k];
      if (raw != null && raw !== '') {
        const n = Number(raw);
        if (!Number.isNaN(n)) {
          if (n >= 100) return Math.abs(n / 1000);
          return Math.abs(n);
        }
      }
    }
    return 0;
  };

  // Ndërton vlera për grafik (MWh * 1000 = kWh për shkallë) nga readings – gjatësi si getChartLabels
  const buildChartValuesFromReadings = (readings, period) => {
    const emptyForPeriod = () => {
      if (period === 'day') return Array(24).fill(0);
      if (period === 'week') return Array(7).fill(0);
      if (period === 'month') return Array(30).fill(0);
      if (period === 'year') return Array(12).fill(0);
      return Array(24).fill(0);
    };
    if (!readings?.length) {
      return emptyForPeriod();
    }
    const sorted = [...readings].sort((a, b) => new Date(a.ts) - new Date(b.ts));
    const now = new Date();
    const getDelta = (start, end) => {
      const inBucket = sorted.filter((r) => {
        const rt = r.ts ? new Date(r.ts) : null;
        return rt && rt >= start && rt <= end;
      });
      if (inBucket.length < 2) return 0;
      const first = getExportFromPayload(inBucket[0].payload);
      const last = getExportFromPayload(inBucket[inBucket.length - 1].payload);
      return Math.max(0, last - first);
    };
    if (period === 'day') {
      return Array.from({ length: 24 }, (_, h) => {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, 0, 0);
        const end = new Date(start.getTime() + 60 * 60 * 1000 - 1);
        return getDelta(start, end) * 1000; // MWh -> kWh për shkallë
      });
    }
    if (period === 'week') {
      // Java aktuale: E Hënë–E Diel, që etiketat Hën, Mar, … të përputhen me ditët
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayOfWeek = d.getDay(); // 0=Die, 1=Hën, 2=Mar, ...
      const toMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      d.setDate(d.getDate() + toMonday);
      return Array.from({ length: 7 }, (_, i) => {
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000 - 1);
        return getDelta(dayStart, dayEnd) * 1000;
      });
    }
    if (period === 'month') {
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      return Array.from({ length: 30 }, (_, i) => {
        const day = i + 1;
        if (day > daysInMonth) return 0;
        const start = new Date(now.getFullYear(), now.getMonth(), day);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
        return getDelta(start, end) * 1000;
      });
    }
    if (period === 'year') {
      return Array.from({ length: 12 }, (_, m) => {
        const start = new Date(now.getFullYear(), m, 1);
        const end = new Date(now.getFullYear(), m + 1, 0, 23, 59, 59);
        return getDelta(start, end) * 1000;
      });
    }
    return Array(24).fill(0);
  };

  // Chart data (day/week/month/year). Për solar + companyId përdoren energy_delivered_mwh nga energy meter i kompanisë.
  const getChartData = (type, periodOrChartIndex, companyId = null) => {
    const period = typeof periodOrChartIndex === 'string' ? periodOrChartIndex : timePeriod;
    const cid = companyId != null ? String(companyId) : null;
    if (type === 'solar' && cid && companyChartData[cid]?.[period]) {
      return companyChartData[cid][period];
    }
    return [];
  };

  // Etiketat për grafikun sipas periudhës. period: për desktop mund të jetë sipas kompanisë.
  const getChartLabels = (period) => {
    const p = period ?? timePeriod;
    if (p === 'day') return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0') + ':00');
    if (p === 'week') return ['Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht', 'Die'];
    if (p === 'month') return Array.from({ length: 30 }, (_, i) => String(i + 1));
    return ['Jan', 'Shk', 'Mar', 'Pri', 'Maj', 'Qer', 'Kor', 'Gush', 'Sht', 'Tet', 'Nën', 'Dhj'];
  };

  // Fetch readings nga energy meter i kompanisë për grafikat e vogla. Përdoret vetëm energy meter i asaj kompanie (cid).
  // Përditësojmë vetëm nëse përgjigja përputhet me spec-in aktual (cid + meterId + period) – nuk i heqim të dhënat kur efekti ridrejtohet.
  useEffect(() => {
    const isMobileNow = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobileNow) return;
    const headers = { Authorization: `Bearer ${getToken()}` };
    const startIndex = companyCarouselIndex * 2;
    const toFetch = companies.slice(startIndex, startIndex + 2);
    const emptyChart = () => [];
    const pendingZeroUpdates = {};
    chartFetchSpecRef.current = {};
    toFetch.forEach((company) => {
      const cid = String(company.id);
      const period = timePeriodByCompanyId[cid] ?? 'day';
      const chartCacheKey = `${cid}|${period}`;
      const lastChartFetchTs = companyChartFetchTsRef.current[chartCacheKey] || 0;
      if (Date.now() - lastChartFetchTs < 90 * 1000) return;
      const ems = energyMetersByCompanyId[cid];
      if (!ems?.length) {
        pendingZeroUpdates[cid] = { [period]: emptyChart() };
        return;
      }
      const meterId = ems[0].id;
      chartFetchSpecRef.current[cid] = { meterId, period };
      const now = new Date();
      let dateFrom, dateTo;
      if (period === 'day') {
        dateFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T00:00:00`;
        dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T23:59:59`;
      } else if (period === 'week') {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const dayOfWeek = d.getDay();
        const toMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        d.setDate(d.getDate() + toMonday);
        const sunday = new Date(d);
        sunday.setDate(sunday.getDate() + 6);
        dateFrom = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00`;
        dateTo = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}T23:59:59`;
      } else if (period === 'month') {
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        dateFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01T00:00:00`;
        dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59`;
      } else {
        dateFrom = `${now.getFullYear()}-01-01T00:00:00`;
        dateTo = `${now.getFullYear()}-12-31T23:59:59`;
      }
      const companyIdForRequest = cid;
      axios
        .get(
          `${BACKEND_URL}/api/devices/${meterId}/pi-energy-meter-readings?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`,
          { headers }
        )
        .then((res) => {
          const spec = chartFetchSpecRef.current[companyIdForRequest];
          if (!spec || spec.meterId !== meterId || spec.period !== period) return;
          const readings = res.data?.readings ?? [];
          const values = buildChartValuesFromReadings(readings, period);
          setCompanyChartData((prev) => ({
            ...prev,
            [companyIdForRequest]: { ...(prev[companyIdForRequest] || {}), [period]: values },
          }));
          companyChartFetchTsRef.current[chartCacheKey] = Date.now();
        })
        .catch(() => {
          const spec = chartFetchSpecRef.current[companyIdForRequest];
          if (!spec || spec.meterId !== meterId || spec.period !== period) return;
          setCompanyChartData((prev) => ({
            ...prev,
            [companyIdForRequest]: { ...(prev[companyIdForRequest] || {}), [period]: emptyChart() },
          }));
          companyChartFetchTsRef.current[chartCacheKey] = Date.now();
        });
    });
    if (Object.keys(pendingZeroUpdates).length > 0) {
      setCompanyChartData((prev) => {
        const next = { ...prev };
        Object.keys(pendingZeroUpdates).forEach((cid) => {
          next[cid] = { ...(next[cid] || {}), ...pendingZeroUpdates[cid] };
        });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, companyCarouselIndex, timePeriodByCompanyId, energyMetersByCompanyId]);

  // Mobile (pamja kompani): "Production 7 days" përdor `companyChartData[cid].week` – efekti desktop anashkalon mobile dhe `.week` s’mbushej kurrë
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth >= 768) return;
    if (mobileView !== 'companies') return;
    const cid = String((selectedCompany || companies[0])?.id || '');
    if (!cid || !getToken()) return;
    const headers = { Authorization: `Bearer ${getToken()}` };
    const ems = energyMetersByCompanyId[cid];
    if (!ems?.length) {
      setCompanyChartData((prev) => ({
        ...prev,
        [cid]: { ...(prev[cid] || {}), week: Array(7).fill(0) },
      }));
      return;
    }
    const meterId = ems[0].id;
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = d.getDay();
    const toMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    d.setDate(d.getDate() + toMonday);
    const sunday = new Date(d);
    sunday.setDate(sunday.getDate() + 6);
    const pad2 = (n) => String(n).padStart(2, '0');
    const dateFrom = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T00:00:00`;
    const dateTo = `${sunday.getFullYear()}-${pad2(sunday.getMonth() + 1)}-${pad2(sunday.getDate())}T23:59:59`;
    let cancelled = false;
    axios
      .get(
        `${BACKEND_URL}/api/devices/${meterId}/pi-energy-meter-readings?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`,
        { headers }
      )
      .then((res) => {
        if (cancelled) return;
        const readings = res.data?.readings ?? [];
        const values = buildChartValuesFromReadings(readings, 'week');
        setCompanyChartData((prev) => ({
          ...prev,
          [cid]: { ...(prev[cid] || {}), week: values },
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setCompanyChartData((prev) => ({
          ...prev,
          [cid]: { ...(prev[cid] || {}), week: Array(7).fill(0) },
        }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileView, selectedCompany?.id, companies, energyMetersByCompanyId]);

  const mobileCompanyId = String(selectedCompany?.id || '');
  const mobilePrimaryMeterId = energyMetersByCompanyId[mobileCompanyId]?.[0]?.id || null;

  // Mobile production: data ditore e ndryshueshme (nga 00:00 deri në fund të ditës / deri tani nëse është sot)
  useEffect(() => {
    if (mobileView !== 'production') return;
    if (!mobileCompanyId) {
      setMobileDailyEnergyData([]);
      return;
    }
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const selectedDate = mobileProductionDate || todayStr;
    const isToday = selectedDate === todayStr;
    const dateFrom = `${selectedDate}T00:00:00`;
    const dateTo = isToday ? `${selectedDate}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}` : `${selectedDate}T23:59:59`;
    const headers = { Authorization: `Bearer ${getToken()}` };
    let cancelled = false;

    setMobileDailyEnergyLoading(true);
    const meterRequest = mobilePrimaryMeterId
      ? axios.get(
        `${BACKEND_URL}/api/devices/${mobilePrimaryMeterId}/pi-energy-meter-readings?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`,
        { headers }
      )
      : Promise.resolve({ data: { readings: [] } });

    Promise.allSettled([meterRequest])
      .then((results) => {
        if (cancelled) return;
        const meterResult = results[0];
        const readings =
          meterResult?.status === 'fulfilled' && Array.isArray(meterResult.value?.data?.readings)
            ? meterResult.value.data.readings
            : [];
        const sorted = [...readings].sort((a, b) => new Date(a.ts) - new Date(b.ts));
        const hourCount = isToday ? now.getHours() + 1 : 24;
        const byHourPower = Array.from({ length: hourCount }, () => []);
        const byHourExport = Array.from({ length: hourCount }, () => ({ first: null, last: null }));
        const selectedDay = new Date(`${selectedDate}T00:00:00`);
        sorted.forEach((r) => {
          const d = r?.ts ? new Date(r.ts) : null;
          if (!d || Number.isNaN(d.getTime())) return;
          if (
            d.getFullYear() !== selectedDay.getFullYear() ||
            d.getMonth() !== selectedDay.getMonth() ||
            d.getDate() !== selectedDay.getDate()
          ) return;
          const h = d.getHours();
          if (h < 0 || h >= hourCount) return;
          const powerMw = getPowerMwFromPayload(r.payload);
          if (Number.isFinite(powerMw) && powerMw !== 0) byHourPower[h].push(powerMw);
          const exportMwh = getExportFromPayload(r.payload);
          if (Number.isFinite(exportMwh)) {
            if (byHourExport[h].first == null) byHourExport[h].first = exportMwh;
            byHourExport[h].last = exportMwh;
          }
        });
        const chart = byHourPower.map((arr, h) => {
          const avgPower = arr.length ? arr.reduce((s, v) => s + (Number(v) || 0), 0) / arr.length : 0;
          const ex = byHourExport[h];
          const deltaMwh = ex.first != null && ex.last != null ? Math.max(0, ex.last - ex.first) : 0;
          const val = avgPower > 0 ? avgPower : deltaMwh;
          return { time: `${pad(h)}:00`, value: Number.isFinite(val) ? val : 0 };
        });
        setMobileDailyEnergyData(chart);
      })
      .finally(() => {
        if (!cancelled) setMobileDailyEnergyLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileView, mobileCompanyId, mobilePrimaryMeterId, mobileProductionDate]);

  // Mobile prodhim: PV tani (MW) për çdo data logger, energy meter dhe inverter të kompanisë
  useEffect(() => {
    if (mobileView !== 'production') return;
    const cid = String(selectedCompany?.id || '');
    if (!cid || !getToken()) {
      setMobileProductionPvNowByDeviceId({});
      return;
    }
    const dlIds = allDataLoggers.filter((dl) => String(dl.company_id) === cid).map((dl) => dl.id).filter(Boolean);
    const emIds = (energyMetersByCompanyId[cid] || []).map((e) => e.id).filter(Boolean);
    const invIds = allInverters.filter((inv) => dlIds.includes(inv.data_logger_id)).map((i) => i.id).filter(Boolean);
    let cancelled = false;
    const hdr = { Authorization: `Bearer ${getToken()}` };

    const fetchPvNow = () => {
      if (dlIds.length === 0 && emIds.length === 0 && invIds.length === 0) {
        setMobileProductionPvNowByDeviceId({});
        return;
      }
      axios
        .post(
          `${BACKEND_URL}/api/devices/pi-readings/latest-batch`,
          { datalogger_ids: dlIds, energy_meter_ids: emIds, inverter_ids: invIds },
          { headers: hdr }
        )
        .then((res) => {
          if (cancelled) return;
          const byId = res.data?.by_device_id || {};
          const next = {};
          [...dlIds, ...emIds, ...invIds].forEach((id) => {
            const readings = byId[id]?.readings || [];
            const mw = getPowerMwFromPayload(readings[0]?.payload || {});
            next[id] = Number.isFinite(Number(mw)) ? Number(mw) : 0;
          });
          setMobileProductionPvNowByDeviceId(next);
        })
        .catch(() => {
          if (cancelled) return;
          setMobileProductionPvNowByDeviceId({});
        });
    };

    fetchPvNow();
    const tid = setInterval(fetchPvNow, MOBILE_PV_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(tid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileView, selectedCompany?.id, allDataLoggers, energyMetersByCompanyId, allInverters]);

  // 8 tema ngjyrash për grafikët desktop – solid (pa hije), mbaj 3s ndryshon të gjitha
  const desktopChartThemes = [
    { fill: '#3b82f6', stroke: '#3b82f6' },
    { fill: '#06b6d4', stroke: '#06b6d4' },
    { fill: '#8b5cf6', stroke: '#8b5cf6' },
    { fill: '#10b981', stroke: '#10b981' },
    { fill: '#ec4899', stroke: '#ec4899' },
    { fill: '#f97316', stroke: '#f97316' },
    { fill: '#eab308', stroke: '#eab308' },
    { fill: '#ef4444', stroke: '#ef4444' },
  ];

  // Get actual inverter count for a company
  const getCompanyInverterCount = (companyId) => {
    // Use allDataLoggers to find all data loggers for this company
    const companyDataLoggers = allDataLoggers.filter(dl => dl.company_id === companyId);
    const dlIds = companyDataLoggers.map(dl => dl.id);
    // Use allInverters (not filtered inverters) to count correctly
    const count = allInverters.filter(inv => dlIds.includes(inv.data_logger_id)).length;
    return count;
  };

  // ==================== MOBILE VIEWS ====================

  // Real stats për kompani (nga data logger readings). Kur s'ka të dhëna reale, kthen null.
  /** Mos përdor Number(null)===0 — përndryshe fsheh të dhënat DL kur energy stats janë { pvNowMw: null }. */
  const isUsableEnergyNumber = (v) => v != null && v !== '' && Number.isFinite(Number(v));

  /** HIB: PV Now / Online = Ppv (kW inverterësh) si te diagrami, jo pv_now_mw nga matësi. */
  const getHibPpvMwForCompany = (companyId) => {
    const cidKey = companyId != null ? String(companyId) : '';
    const flow = hibPowerFlowByCompanyId[cidKey];
    if (flow != null && Number.isFinite(Number(flow.ppvKw))) {
      return Number(flow.ppvKw) / 1000;
    }
    const real = companyRealStats[companyId] ?? companyRealStats[cidKey];
    const mw = Number(real?.activePowerMW);
    return Number.isFinite(mw) ? Math.max(0, mw) : null;
  };

  const resolvePvYieldMwForCompany = (companyId, defaultMw) => {
    const cidKey = companyId != null ? String(companyId) : '';
    const company = companies.find((c) => String(c.id) === cidKey);
    if (!company || !isHibDashboardCompanyName(company.name)) return defaultMw;
    const hibPpvMw = getHibPpvMwForCompany(companyId);
    return hibPpvMw != null ? hibPpvMw : defaultMw;
  };

  const getRealCompanyStats = (companyId) => {
    const dlCount = getCompanyDataLoggerCount(companyId);
    const onlineCount = allDataLoggers.filter(dl => dl.company_id === companyId && dl.status === 'ONLINE').length;
    const cidKey = companyId != null ? String(companyId) : '';
    const real = companyRealStats[companyId] ?? companyRealStats[cidKey];
    const energy = companyEnergyStats[cidKey];
    const hasEnergyStats =
      isUsableEnergyNumber(energy?.pvNowMw) ||
      isUsableEnergyNumber(energy?.pvTodayMWh) ||
      isUsableEnergyNumber(energy?.pvYesterdayMWh);
    if (real || hasEnergyStats) {
      const pvYieldMW = resolvePvYieldMwForCompany(
        companyId,
        isUsableEnergyNumber(energy?.pvNowMw) ? Number(energy.pvNowMw) : (real?.activePowerMW ?? null),
      );
      const todayMWh = isUsableEnergyNumber(energy?.pvTodayMWh) ? Number(energy.pvTodayMWh) : (real?.todayMWh ?? null);
      const yesterdayMWh = isUsableEnergyNumber(energy?.pvYesterdayMWh)
        ? Number(energy.pvYesterdayMWh)
        : (Number.isFinite(Number(real?.yesterdayKwh)) ? Number(real.yesterdayKwh) / 1000 : null);
      const co2Today = isUsableEnergyNumber(energy?.co2TodayKg) ? Number(energy.co2TodayKg) : (real?.co2Today ?? null);
      const totalMWh = real?.totalMWh ?? null;
      const profitToday = real?.profitToday ?? (Number.isFinite(todayMWh) ? todayMWh * 1000 * 0.12 : null);
      return {
        isReal: true,
        plantYield: Number.isFinite(Number(totalMWh)) ? Math.round(Number(totalMWh) * 1000) : null,
        pvYield: Number.isFinite(Number(pvYieldMW)) ? Number(pvYieldMW) * 1000 : null,
        pvYieldMW,
        todayGeneration: Number.isFinite(Number(todayMWh)) ? Number(todayMWh) * 1000 : null,
        todayMWh,
        totalMWh,
        yesterdayGenerationMWh: yesterdayMWh,
        yesterdayGeneration: Number.isFinite(Number(yesterdayMWh)) ? Number(yesterdayMWh) * 1000 : null,
        profit: Number.isFinite(Number(profitToday)) ? Math.round(Number(profitToday) * 100) / 100 : null,
        co2Today: Number.isFinite(Number(co2Today)) ? Math.round(Number(co2Today)) : null,
        lastUpdatedTs: energy?.lastUpdatedTs ?? real?.lastUpdatedTs ?? null,
        status: Number.isFinite(Number(pvYieldMW)) && Math.abs(Number(pvYieldMW)) >= PV_PRODUCTION_ACTIVE_MW ? 'Online' : 'Offline',
        onlineCount,
        totalCount: dlCount
      };
    }
    const pvFallbackMw = resolvePvYieldMwForCompany(
      companyId,
      isUsableEnergyNumber(energy?.pvNowMw) ? Number(energy.pvNowMw) : null,
    );
    return {
      isReal: false,
      plantYield: null,
      pvYield: null,
      pvYieldMW: pvFallbackMw,
      todayGeneration: null,
      todayMWh: null,
      totalMWh: null,
      yesterdayGenerationMWh: null,
      yesterdayGeneration: null,
      profit: null,
      co2Today: null,
      lastUpdatedTs: null,
      status: pvFallbackMw != null && Math.abs(pvFallbackMw) >= PV_PRODUCTION_ACTIVE_MW ? 'Online' : 'Offline',
      onlineCount,
      totalCount: dlCount
    };
  };

  /**
   * kW për EnergyPowerFlowDiagram (HIB): i njëjti burim si EnergyMeterDetailChint `powerFlowKw`
   * — `buildPowerFlowDiagramValuesHibDash` mbi leximet `latest-batch` (inverter + EM).
   * Fallback i shkurtër derisa të ngarkohen të dhënat.
   */
  const getHibEnergyFlowKwForCompany = (companyId) => {
    const cidKey = companyId != null ? String(companyId) : '';
    const fromPi = hibPowerFlowByCompanyId[cidKey];
    if (fromPi != null && typeof fromPi === 'object') return fromPi;
    const real = companyRealStats[companyId] ?? companyRealStats[cidKey];
    const energy = companyEnergyStats[cidKey];
    const ppvKw = Math.max(0, Number(real?.activePowerMW) || 0) * 1000;
    const hasMeterSplit =
      energy?.meterImportKw != null &&
      Number.isFinite(Number(energy.meterImportKw)) &&
      energy?.meterExportKw != null &&
      Number.isFinite(Number(energy.meterExportKw));
    let importKw;
    let exportKw;
    if (hasMeterSplit) {
      importKw = Math.max(0, Number(energy.meterImportKw));
      exportKw = Math.max(0, Number(energy.meterExportKw));
    } else {
      let meterAbsKw = 0;
      if (isUsableEnergyNumber(energy?.pvNowMw)) meterAbsKw = Number(energy.pvNowMw) * 1000;
      importKw = Math.max(0, meterAbsKw - ppvKw);
      exportKw = 0;
    }
    const loadKw = ppvKw + importKw - exportKw;
    return { ppvKw, batteryKw: 0, loadKw, gridImportKw: importKw, gridExportKw: exportKw };
  };

  /** Overlay mbi foto: HIB → EnergyPowerFlowDiagram; përndryshe Solar → Inverter → GRID. */
  const EnergyFlowPhotoOverlay = ({
    displayMW: _displayMW,
    co2Today: _co2Today,
    gridStatus: _gridStatus,
    lastUpdated: _lastUpdated,
    companyName = '',
    inverterCount = 0,
    capacityKW = 0,
    gridPowerMW = null,
    showCompanyName = false,
    flowActive = false,
    hibEnergyFlow = null,
  }) => {
    if (hibEnergyFlow) {
      return (
        <div
          className="absolute inset-0 z-[1] flex min-h-0 min-w-0 items-center justify-center p-1 pointer-events-none"
          aria-hidden="true"
        >
          <EnergyPowerFlowDiagram
            className="ef3d-dashboard-overlay h-full w-full max-h-full min-h-0 min-w-0"
            isDark
            displayPowerUnit="kw"
            ppvKw={hibEnergyFlow.ppvKw}
            batteryKw={hibEnergyFlow.batteryKw}
            loadKw={hibEnergyFlow.loadKw}
            gridImportKw={hibEnergyFlow.gridImportKw}
            gridExportKw={hibEnergyFlow.gridExportKw}
          />
        </div>
      );
    }
    const gridAbsMwStr = formatAbsMw(gridPowerMW);
    const cap = Number(capacityKW);
    const totalMw = Number.isFinite(cap) && cap > 0 ? (cap / 1000).toFixed(1) : '';
    const invCount = Number(inverterCount) || 0;
    const showSummary = invCount > 0 || String(totalMw).trim() !== '';
    return (
      <div className="absolute inset-0 z-[1] flex min-h-0 min-w-0 pointer-events-none" aria-hidden="true">
        <SolarInverterGridFlow
          className="sigf-root--dashboard h-full w-full min-h-0 min-w-0"
          totalMw={totalMw}
          inverterCount={invCount}
          gridMw={gridAbsMwStr != null ? gridAbsMwStr : '0.00'}
          gridUnit="MW"
          flowActive={flowActive}
          exportTagLabel={t('emPowerFlowExportTag')}
          inverterLabel={t('inverter')}
          showSummaryFooter={showSummary}
          brandLabel={showCompanyName && companyName ? companyName : ''}
        />
      </div>
    );
  };

  // Mobile: Companies List - With Dropdown selector
  const MobileCompaniesView = () => {
    // Set first company as selected if none selected
    const currentCompany = selectedCompany || companies[0];
    const capacity = getCompanyCapacity(currentCompany?.id);
    const dlCount = getCompanyDataLoggerCount(currentCompany?.id);
    const stats = getRealCompanyStats(currentCompany?.id);
    const hibEnergyFlow = isHibDashboardCompanyName(currentCompany?.name)
      ? getHibEnergyFlowKwForCompany(currentCompany?.id)
      : null;
    const pvDisplayMw = formatAbsMw(stats?.pvYieldMW);
    const chartLookupId = currentCompany?.id != null ? String(currentCompany.id) : '';
    const weekDataMobile = companyChartData[chartLookupId]?.week ?? Array(7).fill(0);

    // Get first data logger of this company for navigation
    const companyDataLoggers = dataLoggers.filter(dl => dl.company_id === currentCompany?.id);
    const firstDataLogger = companyDataLoggers[0];

    return (
      <div className="h-full flex flex-col bg-[#f5f5f5] dark:bg-gradient-to-b dark:from-[#0f172a] dark:to-black">
        {/* Header – dropdown si te Pajisjet; lartësi fikse që të përputhet me pamjen Detaje */}
        <div className="px-3 pt-3 pb-2 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a] flex-shrink-0">
          <div className="flex items-center justify-between min-h-8">
            <div className="relative flex-1 min-w-0 flex items-center min-h-8">
              <button
                type="button"
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                className="flex items-center gap-2 w-full text-left min-h-8"
              >
                <span className="text-lg font-bold text-gray-900 dark:text-white truncate">
                  {currentCompany?.name || (companies.length === 0 ? t('noCompaniesDash') : t('loading'))}
                </span>
                <svg className={`w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showCompanyDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-transparent">
                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        navigate('/dashboard/pv-fleet');
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-purple-900/25 transition border-b border-gray-100 dark:border-[#2d3a4f] text-purple-700 dark:text-purple-300 font-semibold text-sm"
                    >
                      {t('pvFleetAllCompanies')}
                    </button>
                  )}
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => {
                        setSelectedCompany(company);
                        if (company) selectCompanyToken(company.id);
                        setShowCompanyDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition border-b border-gray-100 dark:border-[#2d3a4f] last:border-0 ${String(currentCompany?.id) === String(company.id)
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium'
                        : 'text-gray-700 dark:text-white'
                        }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className={`px-2.5 py-1 text-xs rounded-full font-medium ml-2 shrink-0 ${stats.status === 'Online' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-red-500/20 text-red-500 dark:text-red-400'}`}>
              {(stats.status === 'Online' ? t('online') : t('offline')).toUpperCase()}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{formatCapacity(capacity)} MW • {dlCount} Data Logger</p>
        </div>

        {/* Content – flex për të mbushur ekranin pa scroll (foto + grafiku marrin hapësirën e lirë) */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden overflow-x-hidden px-3 pt-2 pb-2">
          {/* Foto kompanie – rritet me flex për të mbushur hapësirën poshtë statistikave */}
          <div
            onClick={() => {
              if (firstDataLogger) {
                setSelectedDataLogger(firstDataLogger);
                setMobileView('production');
              }
            }}
            className="relative w-full min-h-[100px] flex-[1.15] basis-0 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-800 dark:to-slate-900 rounded-xl overflow-hidden cursor-pointer mb-1.5 shadow-lg"
          >
            <img src={getPhotoUrl(currentCompany, 'company')} alt={currentCompany?.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <EnergyFlowPhotoOverlay
              displayMW={pvDisplayMw != null ? `${pvDisplayMw} MW` : '—'}
              co2Today={stats?.co2Today}
              gridStatus={firstDataLogger?.grid_status || 'ON_GRID'}
              lastUpdated={formatTimeAgo(stats?.lastUpdatedTs)}
              companyName={currentCompany?.name}
              inverterCount={getCompanyInverterCount(currentCompany?.id)}
              capacityKW={getCompanyCapacity(currentCompany?.id)}
              gridPowerMW={Number.isFinite(Number(stats?.pvYieldMW)) ? Number(stats.pvYieldMW) : null}
              flowActive={Number.isFinite(Number(stats?.pvYieldMW)) && Math.abs(Number(stats.pvYieldMW)) >= PV_PRODUCTION_ACTIVE_MW}
              hibEnergyFlow={hibEnergyFlow}
            />
          </div>

          {/* 4 Stats mobile: CO2 Today, PV Now, PV Today, PV Yesterday – light/dark mode */}
          <div className="grid grid-cols-2 gap-2 mb-1.5 flex-shrink-0">
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-2.5 text-center shadow-sm border border-gray-200 dark:border-slate-700/50 min-h-[3.5rem] flex flex-col justify-center">
              <p className="text-[8px] text-gray-600 dark:text-white/80 uppercase leading-tight">CO2 Today</p>
              <div className="min-h-[1.5rem] flex flex-col justify-center">
                <p className="text-[13px] font-bold text-emerald-600 dark:text-[#86efac] leading-tight">{Number.isFinite(Number(stats?.co2Today)) ? `${Number(stats.co2Today).toFixed(0)} kg` : '—'}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-2.5 text-center shadow-sm border border-gray-200 dark:border-slate-700/50 min-h-[3.5rem] flex flex-col justify-center">
              <p className="text-[8px] text-gray-600 dark:text-white/80 uppercase leading-tight">PV Now</p>
              <div className="min-h-[1.5rem] flex flex-col justify-center">
                <p className="text-[13px] font-bold text-cyan-600 dark:text-[#34D3F5] leading-tight">
                  {pvDisplayMw ?? '—'}
                </p>
                <p className="text-[7px] text-gray-500 dark:text-white/70">MW</p>
              </div>
            </div>
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-2.5 text-center shadow-sm border border-gray-200 dark:border-slate-700/50 min-h-[3.5rem] flex flex-col justify-center">
              <p className="text-[8px] text-gray-600 dark:text-white/80 uppercase leading-tight">PV Today</p>
              <div className="min-h-[1.5rem] flex flex-col justify-center">
                {(() => {
                  const f = formatEnergy(Number.isFinite(Number(stats?.todayGeneration)) ? Number(stats.todayGeneration).toFixed(2) : null);
                  return (<><p className="text-[13px] font-bold text-emerald-600 dark:text-[#86efac] leading-tight">{f.value}</p><p className="text-[7px] text-gray-500 dark:text-white/70">{f.unit}</p></>);
                })()}
              </div>
            </div>
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-2.5 text-center shadow-sm border border-gray-200 dark:border-slate-700/50 min-h-[3.5rem] flex flex-col justify-center">
              <p className="text-[8px] text-gray-600 dark:text-white/80 uppercase leading-tight">PV Yesterday</p>
              <div className="min-h-[1.5rem] flex flex-col justify-center">
                <p className="text-[13px] font-bold text-gray-900 dark:text-white leading-tight">
                  {Number.isFinite(Number(stats?.yesterdayGenerationMWh)) ? Number(stats.yesterdayGenerationMWh).toFixed(2) : '—'}
                </p>
                <p className="text-[7px] text-gray-500 dark:text-white/70">MWh</p>
              </div>
            </div>
          </div>

          {/* Mini Chart – 7 ditë (të dhëna nga energy meter energy_delivered_mwh), total javë + tooltip për ditë */}
          <div
            onClick={() => {
              if (chartLongPressHandledRef.current) {
                chartLongPressHandledRef.current = false;
                return;
              }
              if (chartLongPressTimerRef.current) return;
              if (firstDataLogger) {
                setSelectedDataLogger(firstDataLogger);
                setMobileView('production');
              }
            }}
            onPointerDown={() => {
              chartLongPressHandledRef.current = false;
              chartLongPressTimerRef.current = setTimeout(() => {
                setMobileChartBarTheme(prev => (prev + 1) % 8);
                chartLongPressHandledRef.current = true;
                chartLongPressTimerRef.current = null;
              }, 3000);
            }}
            onPointerUp={() => {
              if (chartLongPressTimerRef.current) {
                clearTimeout(chartLongPressTimerRef.current);
                chartLongPressTimerRef.current = null;
              }
            }}
            onPointerLeave={() => {
              if (chartLongPressTimerRef.current) {
                clearTimeout(chartLongPressTimerRef.current);
                chartLongPressTimerRef.current = null;
              }
              setMobileProductionHoveredBar(null);
            }}
            className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-transparent rounded-xl p-2.5 cursor-pointer flex min-h-0 w-full flex-1 basis-0 flex-col overflow-visible select-none touch-none"
          >
            <div className="mb-1.5 flex shrink-0 justify-between items-center">
              <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Production 7 days (kWh)</span>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-lg"
                style={{
                  color: ['#b45309', '#0e7490', '#6d28d9', '#047857', '#be185d', '#c2410c', '#1d4ed8', '#b45309'][mobileChartBarTheme],
                  backgroundColor: ['#fef3c7', '#cffafe', '#ede9fe', '#d1fae5', '#fce7f3', '#ffedd5', '#dbeafe', '#fef9c3'][mobileChartBarTheme],
                }}
              >
                {(() => {
                  const totalKwh = weekDataMobile.reduce((s, v) => s + (Number(v) || 0), 0);
                  const f = formatEnergy(totalKwh);
                  return `${f.value} ${f.unit}`;
                })()}
              </span>
            </div>
            <div className="flex min-h-[4.5rem] flex-1 flex-row items-stretch justify-between gap-1.5 rounded-xl border border-gray-200 bg-gray-50 p-1.5 dark:border-slate-600/50 dark:bg-[#1e293b]/80">
              {(() => {
                const maxKwh = Math.max(1, ...weekDataMobile.map((v) => Number(v) || 0));
                const dayLabels = ['Hën', 'Mar', 'Mër', 'En', 'Pre', 'Sht', 'Die'];
                const mobileChartThemes = [
                  { bg: 'linear-gradient(180deg, #fde047 0%, #f59e0b 50%, #ea580c 100%)', shadow: '0 -2px 6px rgba(245, 158, 11, 0.35)', border: 'border-amber-300/40 dark:border-amber-500/30' },
                  { bg: 'linear-gradient(180deg, #67e8f9 0%, #06b6d4 50%, #0891b2 100%)', shadow: '0 -2px 6px rgba(6, 182, 212, 0.35)', border: 'border-cyan-300/40 dark:border-cyan-500/30' },
                  { bg: 'linear-gradient(180deg, #a78bfa 0%, #8b5cf6 50%, #7c3aed 100%)', shadow: '0 -2px 6px rgba(139, 92, 246, 0.35)', border: 'border-violet-300/40 dark:border-violet-500/30' },
                  { bg: 'linear-gradient(180deg, #6ee7b7 0%, #10b981 50%, #059669 100%)', shadow: '0 -2px 6px rgba(16, 185, 129, 0.35)', border: 'border-emerald-300/40 dark:border-emerald-500/30' },
                  { bg: 'linear-gradient(180deg, #f472b6 0%, #ec4899 50%, #db2777 100%)', shadow: '0 -2px 6px rgba(236, 72, 153, 0.35)', border: 'border-pink-300/40 dark:border-pink-500/30' },
                  { bg: 'linear-gradient(180deg, #fb923c 0%, #f97316 50%, #ea580c 100%)', shadow: '0 -2px 6px rgba(249, 115, 22, 0.35)', border: 'border-orange-300/40 dark:border-orange-500/30' },
                  { bg: 'linear-gradient(180deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)', shadow: '0 -2px 6px rgba(59, 130, 246, 0.35)', border: 'border-blue-300/40 dark:border-blue-500/30' },
                  { bg: 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)', shadow: '0 -2px 6px rgba(250, 204, 21, 0.35)', border: 'border-yellow-300/40 dark:border-yellow-500/30' },
                ];
                const themeStyle = mobileChartThemes[mobileChartBarTheme];
                return weekDataMobile.map((kwh, i) => {
                  const val = Number(kwh) || 0;
                  const pct = maxKwh > 0 ? Math.max(6, (val / maxKwh) * 100) : 6;
                  const isHovered = mobileProductionHoveredBar === i;
                  return (
                    <div
                      key={i}
                      className="relative flex min-h-0 min-w-0 flex-1 flex-col items-center gap-1"
                      onPointerEnter={() => setMobileProductionHoveredBar(i)}
                      onPointerLeave={() => setMobileProductionHoveredBar(null)}
                      onPointerDown={() => setMobileProductionHoveredBar(i)}
                    >
                      {isHovered && (
                        <div className="absolute -top-6 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-1.5 py-0.5 text-[9px] font-medium text-white shadow dark:bg-gray-700">
                          {formatEnergy(val).value} {formatEnergy(val).unit}
                        </div>
                      )}
                      <div className="flex min-h-0 w-full flex-1 flex-col justify-end items-center">
                        <div
                          className={`mx-auto w-full max-w-[14px] min-h-[4px] rounded-t-md border-x border-t transition-all duration-300 ${themeStyle.border}`}
                          style={{
                            height: `${pct}%`,
                            minHeight: '6px',
                            maxHeight: '100%',
                            background: themeStyle.bg,
                            boxShadow: themeStyle.shadow,
                          }}
                        />
                      </div>
                      <span className="w-full shrink-0 truncate text-center text-[8px] font-medium text-gray-500 dark:text-gray-400">
                        {dayLabels[i]}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile: Data Loggers for selected company
  const MobileDataLoggersView = () => {
    const companyDataLoggers = dataLoggers.filter(dl => dl.company_id === selectedCompany?.id);

    return (
      <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-900">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileView('companies')} className="p-1">
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selectedCompany?.name}</h2>
              <p className="text-xs text-gray-500">{companyDataLoggers.length} Data Loggers • {formatCapacity(getCompanyCapacity(selectedCompany?.id))} MW</p>
            </div>
          </div>
        </div>

        {/* Data Loggers List - Card style with full logo */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {companyDataLoggers.map((dl) => {
            const capacityMW = formatCapacity(dl.installed_capacity_kw);

            return (
              <div
                key={dl.id}
                onClick={() => {
                  // Go directly to devices page filtered by this company
                  selectedCompany?.id ? navigate(`/devices?company=${selectedCompany.id}`) : navigate('/devices');
                }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden cursor-pointer"
              >
                {/* Logo/Photo Area - Full visible with contain, taller */}
                <div className="relative h-48 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 overflow-hidden">
                  <img
                    src={getPhotoUrl(dl, 'data-logger')}
                    alt={dl.name}
                    className="absolute inset-0 w-full h-full object-contain p-3"
                  />
                </div>

                {/* Info Section */}
                <div className="p-4">
                  {/* Name and Company */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{dl.name}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedCompany?.name}</p>
                    </div>
                  </div>

                  {/* Status Badges Row */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 ${dl.status === 'ONLINE'
                      ? 'bg-green-50 text-green-600 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700'
                      : 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700'
                      }`}>
                      {dl.status === 'ONLINE' ? t('online') : t('offline')}
                    </span>
                    <span className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 ${dl.grid_status === 'ON_GRID'
                      ? 'bg-yellow-50 text-yellow-600 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700'
                      : 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                      }`}>
                      {dl.grid_status === 'ON_GRID' ? t('onGrid') : t('offGrid')}
                    </span>
                  </div>

                  {/* Capacity */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Capacity:</span>
                    <span className="px-3 py-1.5 text-sm font-bold rounded-lg bg-cyan-50 text-cyan-600 border-2 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-700">
                      {capacityMW} MW
                    </span>
                  </div>

                  {/* Serial Number */}
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    SN: {dl.serial_number}
                  </div>
                </div>
              </div>
            );
          })}

          {companyDataLoggers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No Data Loggers</p>
              <p className="text-xs">Shto një data logger për këtë kompani</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Mobile: Production view for selected data logger – header i njëjtë si lista (që kompania të mos zhvendoset)
  const MobileProductionView = () => {
    const capacity = getCompanyCapacity(selectedCompany?.id);
    const dlCount = getCompanyDataLoggerCount(selectedCompany?.id);
    const stats = getRealCompanyStats(selectedCompany?.id);
    const companyId = selectedCompany?.id;
    const companyDataLoggers = allDataLoggers.filter((dl) => String(dl.company_id) === String(companyId));
    const companyDataLoggerIds = companyDataLoggers.map((dl) => dl.id);
    const companyEnergyMeters = energyMetersByCompanyId[String(companyId)] || [];
    const companyInverters = allInverters.filter((inv) => companyDataLoggerIds.includes(inv.data_logger_id));
    const pvNowMwForDevice = (deviceId) => {
      const key = deviceId != null ? String(deviceId) : '';
      const v = mobileProductionPvNowByDeviceId[key];
      return Number.isFinite(Number(v)) ? Number(v) : 0;
    };
    const pvFlowMw = companyDataLoggerIds.reduce((s, dlId) => s + Math.abs(pvNowMwForDevice(dlId)), 0);
    const gridEmProductionMw = companyEnergyMeters.reduce((s, em) => s + Math.abs(pvNowMwForDevice(em.id)), 0);
    const mobilePvFlowActive = Math.abs(pvFlowMw) >= PV_PRODUCTION_ACTIVE_MW;
    const mobileGridFlowActive = gridEmProductionMw >= PV_PRODUCTION_ACTIVE_MW;
    const pvPowerKw = Number.isFinite(pvFlowMw) ? Math.abs(pvFlowMw) * 1000 : null;
    const todayEnergyKwh = Number.isFinite(Number(stats?.todayGeneration)) ? Number(stats.todayGeneration) : null;
    const isLiveDeviceBadge = (status) => {
      const u = String(status || '').toUpperCase();
      return u === 'ONLINE' || u === 'ACTIVE' || u === 'RUNNING' || u === 'ON';
    };
    const todayStr = formatLocalYmd(new Date());
    const canGoNextDay = mobileProductionDate < todayStr;
    const prettyDate = (() => {
      const d = new Date(`${mobileProductionDate}T00:00:00`);
      return Number.isNaN(d.getTime())
        ? mobileProductionDate
        : d.toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    })();

    const ringTrackStroke = chartThemeDark ? '#374151' : '#d1d5db';
    const chartGridStroke = chartThemeDark ? '#334155' : '#e5e7eb';
    const chartTickFill = chartThemeDark ? '#9ca3af' : '#6b7280';
    const chartTooltipStyle = chartThemeDark
      ? { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }
      : { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827' };

    return (
      <div className="h-full flex flex-col bg-[#f5f5f5] dark:bg-gradient-to-b dark:from-[#0f172a] dark:to-black">
        {/* Header identik me MobileCompaniesView – lartësi e njëjtë që emri të mos zhvendoset */}
        <div className="px-3 pt-3 pb-2 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a] flex-shrink-0">
          <div className="flex items-center justify-between min-h-8">
            <div className="flex-1 min-w-0 flex items-center min-h-8">
              <span className="text-lg font-bold text-gray-900 dark:text-white truncate block leading-tight">
                {selectedCompany?.name || selectedDataLogger?.name}
              </span>
            </div>
            <span className={`px-2.5 py-1 text-xs rounded-full font-medium ml-2 shrink-0 ${stats.status === 'Online' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-red-500/20 text-red-500 dark:text-red-400'}`}>
              {(stats.status === 'Online' ? t('online') : t('offline')).toUpperCase()}
            </span>
            <button
              onClick={() => setMobileView('companies')}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600 ml-2 flex-shrink-0"
              aria-label={t('close')}
            >
              ✕
            </button>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{formatCapacity(capacity)} MW • {dlCount} Data Logger</p>
        </div>

        {/* Scrollable Content — light: sfond i njëjtë me prindin; dark: transparent mbi gradient */}
        <div className="flex-1 overflow-y-auto p-3 bg-[#f5f5f5] dark:bg-transparent">
          {/* Energy Flow */}
          <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-4 mb-4 shadow-sm">
            <h3 className="text-gray-900 dark:text-white text-sm font-semibold mb-3 text-center">Energy Flow</h3>
            <div className="flex justify-between items-center">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto relative">
                  <svg className="transform -rotate-90 w-24 h-24">
                    <circle cx="48" cy="48" r="42" stroke={ringTrackStroke} strokeWidth="5" fill="none" />
                    <circle cx="48" cy="48" r="42" stroke={chartColors.solar} strokeWidth="5" fill="none" strokeDasharray="264 264" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">PV</span>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">{Math.abs(pvFlowMw).toFixed(2)}</span>
                    <span className="text-[9px] text-gray-500 dark:text-gray-400">MW</span>
                  </div>
                </div>
              </div>
              <div className="flex-1 px-3">
                <div className="h-1.5 bg-gradient-to-r from-yellow-500 via-cyan-500 to-purple-500 rounded relative overflow-hidden">
                  {mobilePvFlowActive && (
                    <div className="absolute top-0 left-0 w-5 h-1.5 bg-white/80 rounded" style={{ animation: 'flowRight 2s linear infinite' }} />
                  )}
                </div>
                {mobilePvFlowActive && (
                  <style>{`@keyframes flowRight { 0% { left: 0; } 100% { left: calc(100% - 20px); } }`}</style>
                )}
              </div>
              <div className="text-center">
                <div className="w-24 h-24 mx-auto relative">
                  <svg className="transform -rotate-90 w-24 h-24">
                    <circle cx="48" cy="48" r="42" stroke={ringTrackStroke} strokeWidth="5" fill="none" />
                    <circle cx="48" cy="48" r="42" stroke={chartColors.load} strokeWidth="5" fill="none" strokeDasharray={`${(mobileGridFlowActive ? 100 : 0) * 2.64} 264`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Grid</span>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">{gridEmProductionMw.toFixed(2)}</span>
                    <span className="text-[9px] text-gray-500 dark:text-gray-400">MW</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Energy Analysis (today -> now) nga energy meter */}
          <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-4 mb-4 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-gray-900 dark:text-white text-sm font-semibold">Analiza e Energjisë</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date(`${mobileProductionDate}T00:00:00`);
                    d.setDate(d.getDate() - 1);
                    setMobileProductionDate(formatLocalYmd(d));
                  }}
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white text-sm"
                  aria-label="Previous day"
                >
                  ‹
                </button>
                <span className="text-[11px] text-gray-700 dark:text-gray-300 min-w-[78px] text-center">{prettyDate}</span>
                <button
                  type="button"
                  disabled={!canGoNextDay}
                  onClick={() => {
                    if (!canGoNextDay) return;
                    const d = new Date(`${mobileProductionDate}T00:00:00`);
                    d.setDate(d.getDate() + 1);
                    const next = formatLocalYmd(d);
                    setMobileProductionDate(next > todayStr ? todayStr : next);
                  }}
                  className={`text-sm ${canGoNextDay ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white' : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'}`}
                  aria-label="Next day"
                >
                  ›
                </button>
              </div>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mobileDailyEnergyData} margin={{ top: 8, right: 10, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mobileDayEnergyFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} vertical={false} />
                  <XAxis
                    dataKey="time"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: chartTickFill, fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: chartTickFill, fontSize: 10 }}
                    tickFormatter={(v) => `${Math.abs(Number(v)).toFixed(2)} MW`}
                  />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    labelFormatter={(label) => `Koha: ${label}`}
                    formatter={(v) => [`${Math.abs(Number(v)).toFixed(2)} MW`, 'Export']}
                  />
                  <Area type="monotone" dataKey="value" stroke="#a855f7" fill="url(#mobileDayEnergyFill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {!mobileDailyEnergyLoading && mobileDailyEnergyData.length === 0 && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 text-center mt-2">Nuk ka të dhëna për sot</p>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-3 text-center shadow-sm">
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">PV Power</p>
              <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{formatPower(pvPowerKw).value}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-500">{formatPower(pvPowerKw).unit || '—'}</p>
            </div>
            <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-3 text-center shadow-sm">
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">Capacity</p>
              <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">{formatCapacity(capacity)}</p>
              <p className="text-[10px] text-gray-500">MW</p>
            </div>
            <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-3 text-center shadow-sm">
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">{t('today')}</p>
              {(() => {
                const val = todayEnergyKwh;
                const f = formatEnergy(val);
                return (<><p className="text-xl font-bold text-green-600 dark:text-green-400">{f.value}</p><p className="text-[10px] text-gray-500">{f.unit || '—'}</p></>);
              })()}
            </div>
            <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-3 text-center shadow-sm">
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">Total</p>
              {(() => { const f = formatEnergy(Number.isFinite(Number(stats.totalMWh)) ? Number(stats.totalMWh) * 1000 : null); return (<><p className="text-xl font-bold text-blue-600 dark:text-blue-400">{f.value}</p><p className="text-[10px] text-gray-500">{f.unit || '—'}</p></>); })()}
            </div>
          </div>

          {/* Device List: Data Logger -> Energy Meter -> Inverter */}
          <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-3 mb-4 shadow-sm">
            <h3 className="text-gray-900 dark:text-white text-sm font-semibold mb-3">Data Loggers ({companyDataLoggers.length})</h3>
            <div className="space-y-2">
              {companyDataLoggers.map((dl) => (
                <div
                  key={dl.id}
                  className="bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-transparent rounded-lg p-2 flex items-center gap-3"
                  onClick={() => navigate(`/devices/${dl.id}`)}
                >
                  <div className="w-10 h-10 bg-cyan-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xs">DL</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 dark:text-white text-sm font-medium">{dl.name}</p>
                    <p className="text-green-700 dark:text-green-400/90 text-[10px] font-medium tabular-nums">
                      {t('pvNowShort')}: {Math.abs(pvNowMwForDevice(dl.id)).toFixed(2)} MW
                    </p>
                  </div>
                  <span className={`px-1.5 py-0.5 text-[9px] rounded shrink-0 ${isLiveDeviceBadge(dl.status) ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400'}`}>
                    {dl.status || 'OFFLINE'}
                  </span>
                </div>
              ))}
              {companyDataLoggers.length === 0 && <p className="text-xs text-gray-500 dark:text-gray-400">Nuk ka Data Logger për këtë kompani</p>}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-3 mb-4 shadow-sm">
            <h3 className="text-gray-900 dark:text-white text-sm font-semibold mb-3">Energy Meters ({companyEnergyMeters.length})</h3>
            <div className="space-y-2">
              {companyEnergyMeters.map((em) => (
                <div
                  key={em.id}
                  className="bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-transparent rounded-lg p-2 flex items-center gap-3"
                  onClick={() => {
                    const deviceId = em.data_logger_id || selectedDataLogger?.id || companyDataLoggers[0]?.id;
                    if (deviceId) {
                      const to = buildEnergyMeterDetailPath({ ...em, data_logger_id: deviceId });
                      if (to) navigate(to);
                    }
                  }}
                >
                  <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 dark:text-white text-sm font-medium">{em.name}</p>
                    <p className="text-green-700 dark:text-green-400/90 text-[10px] font-medium tabular-nums">
                      {t('pvNowShort')}: {Math.abs(pvNowMwForDevice(em.id)).toFixed(2)} MW
                    </p>
                  </div>
                  <span className={`px-1.5 py-0.5 text-[9px] rounded shrink-0 ${isLiveDeviceBadge(em.status) ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400'}`}>
                    {em.status || 'OFFLINE'}
                  </span>
                </div>
              ))}
              {companyEnergyMeters.length === 0 && <p className="text-xs text-gray-500 dark:text-gray-400">Nuk ka Energy Meter për këtë kompani</p>}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-transparent rounded-xl p-3 mb-2 shadow-sm">
            <h3 className="text-gray-900 dark:text-white text-sm font-semibold mb-3">Inverters ({companyInverters.length})</h3>
            <div className="space-y-2">
              {companyInverters.map((inv, idx) => (
                <div
                  key={inv.id}
                  className="bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-transparent rounded-lg p-2 flex items-center gap-3"
                  onClick={() => {
                    const deviceId = inv.data_logger_id || selectedDataLogger?.id || companyDataLoggers[0]?.id;
                    if (deviceId) navigate(`/devices/${deviceId}/inverter/${inv.id}`);
                  }}
                >
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{idx + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 dark:text-white text-sm font-medium">{inv.name}</p>
                    <p className="text-green-700 dark:text-green-400/90 text-[10px] font-medium tabular-nums">
                      {t('pvNowShort')}: {Math.abs(pvNowMwForDevice(inv.id)).toFixed(2)} MW
                    </p>
                  </div>
                  <span className={`px-1.5 py-0.5 text-[9px] rounded shrink-0 ${isLiveDeviceBadge(inv.status) ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400'}`}>
                    {inv.status || 'OFFLINE'}
                  </span>
                </div>
              ))}
              {companyInverters.length === 0 && <p className="text-xs text-gray-500 dark:text-gray-400">Nuk ka Inverter për këtë kompani</p>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== DESKTOP VIEW ====================
  const DesktopView = () => {
    // Calculate total stats for all companies (real nga companyRealStats)
    const totalCapacity = companies.reduce((sum, c) => sum + getCompanyCapacity(c.id), 0);
    const totalMWh = companies.reduce((sum, c) => {
      const s = companyRealStats[c.id];
      return sum + (s?.totalMWh ?? 0);
    }, 0);
    const totalCo2Kg = companies.reduce((sum, c) => {
      const s = companyRealStats[c.id];
      return sum + (s?.co2Today ?? 0);
    }, 0);
    const totalMWhToday = companies.reduce((sum, c) => {
      const s = companyRealStats[c.id];
      return sum + (s?.todayMWh ?? 0);
    }, 0);
    const treeEquiv = totalMWh > 0 ? Math.round((totalMWh * 400) / 22) : 0; // ~22 kg CO2/tree/vit

    // Karusel (web): 2 kompani për faqe – ndërrohet vetëm me pikat / butonat
    const startIndex = companyCarouselIndex * 2;
    const visibleCompanies = companies.slice(startIndex, startIndex + 2);
    const displayCompanies = visibleCompanies.length > 0 ? visibleCompanies : companies.slice(0, 2);
    const totalPages = Math.max(1, Math.ceil(companies.length / 2));

    const renderCompanyCard = (company) => {
      const stats = getRealCompanyStats(company.id);
      const hibEnergyFlow = isHibDashboardCompanyName(company.name)
        ? getHibEnergyFlowKwForCompany(company.id)
        : null;
      const capacity = getCompanyCapacity(company.id);
      const inverterCount = getCompanyInverterCount(company.id);
      const pvMw = Number.isFinite(Number(stats?.pvYieldMW)) ? Number(stats.pvYieldMW) : null;
      const pvCardAbs = formatAbsMw(stats?.pvYieldMW);

      return (
        <div
          key={company.id}
          onClick={() => {
            setSelectedCompany(company);
            company?.id ? navigate(`/devices?company=${company.id}`) : navigate('/devices');
          }}
          className="rounded-2xl overflow-hidden cursor-pointer border transition-all duration-300 hover:scale-[1.02] hover:border-cyan-500/50 bg-white border-gray-200 hover:shadow-lg dark:bg-[#1e293b] dark:border-[#334155]"
        >
          {/* Photo with company name overlay – web: madhësi si grafiku (e rritur) */}
          <div className="relative h-[240px] overflow-hidden">
            <img src={getPhotoUrl(company, 'company')} alt={company.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
            <EnergyFlowPhotoOverlay
              displayMW={pvCardAbs != null ? `${pvCardAbs} MW` : '—'}
              co2Today={stats?.co2Today}
              gridStatus={allDataLoggers.find(dl => dl.company_id === company.id)?.grid_status || 'ON_GRID'}
              lastUpdated={formatTimeAgo(stats?.lastUpdatedTs)}
              companyName={company.name}
              inverterCount={inverterCount}
              capacityKW={capacity}
              gridPowerMW={Number.isFinite(Number(stats?.pvYieldMW)) ? Number(stats.pvYieldMW) : null}
              showCompanyName
              flowActive={pvMw != null && Math.abs(pvMw) >= PV_PRODUCTION_ACTIVE_MW}
              hibEnergyFlow={hibEnergyFlow}
            />
          </div>

          {/* Stats Grid – 4 copa: PV Now, PV Today, PV Yesterday, CO2 Today; light/dark mode */}
          <div className="px-1 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="p-3 rounded-2xl bg-gray-100 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] min-h-[4.5rem] flex flex-col justify-center">
                <p className="text-xs mb-0.5 text-gray-700 dark:text-white">PV Now</p>
                <p className="text-sm font-bold text-cyan-600 dark:text-[#34D3F5]">{pvCardAbs ?? '—'}</p>
                <p className="text-[10px] text-gray-500 dark:text-white/70">MW</p>
              </div>
              <div className="p-3 rounded-2xl bg-gray-100 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] min-h-[4.5rem] flex flex-col justify-center">
                <p className="text-xs mb-0.5 text-gray-700 dark:text-white">PV Today</p>
                <p className="text-sm font-bold text-emerald-600 dark:text-[#86efac]">{Number.isFinite(Number(stats?.todayMWh)) ? Number(stats.todayMWh).toFixed(2) : '—'}</p>
                <p className="text-[10px] text-gray-500 dark:text-white/70">MWh</p>
              </div>
              <div className="p-3 rounded-2xl bg-gray-100 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] min-h-[4.5rem] flex flex-col justify-center">
                <p className="text-xs mb-0.5 text-gray-700 dark:text-white">PV Yesterday</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{Number.isFinite(Number(stats?.yesterdayGenerationMWh)) ? Number(stats.yesterdayGenerationMWh).toFixed(2) : '—'}</p>
                <p className="text-[10px] text-gray-500 dark:text-white/70">MWh</p>
              </div>
              <div className="p-3 rounded-2xl bg-gray-100 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] min-h-[4.5rem] flex flex-col justify-center">
                <p className="text-[10px] mb-0.5 text-gray-700 dark:text-white">CO2 Today</p>
                <p className="text-xs font-bold text-emerald-600 dark:text-[#86efac]">{Number.isFinite(Number(stats?.co2Today)) ? `${Number(stats.co2Today).toFixed(0)} kg` : '—'}</p>
              </div>
            </div>

            {/* Status Row (prodhim PV > prag → online) */}
            <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${stats.status === 'Online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className={`text-xs ${stats.status === 'Online' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{stats.status === 'Online' ? t('online') : t('offline')}</span>
              </div>
              <span className="text-xs text-gray-300 dark:text-gray-500">•</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{stats.onlineCount} Online</span>
              <span className="text-xs text-gray-300 dark:text-gray-500">•</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{stats.totalCount - stats.onlineCount} Offline</span>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="h-full flex flex-col px-2 py-4 overflow-hidden bg-gray-100 dark:bg-[#0f172a] max-w-full">
        {/* Top Row (web): 2 karta + hapësirë e lirë (kolona 3) */}
        <div className="relative mb-4 flex-shrink-0">
          <div className="grid grid-cols-3 items-stretch gap-3">
            {displayCompanies.map(renderCompanyCard)}
            {/* Kolona 3: Production by companies — komponenti nga grafika (gauge); të njëjtat të dhëna si më parë */}
            <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-[#334155] dark:bg-[#0f172a]">
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-1.5 sm:p-2">
                <ProductionByCompanies
                  viewAllLabel={`${t('pvFleetEnergyShort')} →`}
                  companies={(companies.length > 0 ? companies : []).map((company) => {
                    const stats = getRealCompanyStats(company.id);
                    const capKw = getCompanyCapacity(company.id);
                    const cap = Number(capKw) / 1000;
                    const prodRaw = stats?.pvYieldMW;
                    const prod = Number.isFinite(Number(prodRaw)) ? Number(prodRaw) : null;
                    const prodAbsMw = prod != null ? Math.abs(prod) : 0;
                    const safeCap = Number.isFinite(cap) ? cap : 0;
                    const isHib = isHibDashboardCompanyName(company.name);
                    const hibPpvMw = isHib ? getHibPpvMwForCompany(company.id) : null;
                    return {
                      id: company.id,
                      name: company.name,
                      pvNow: prodAbsMw,
                      ppv: hibPpvMw != null ? Math.abs(hibPpvMw) : undefined,
                      capacity: safeCap,
                      online: stats.status === 'Online',
                    };
                  })}
                  onViewAll={() => {
                    if (isSuperAdmin) navigate('/dashboard/pv-fleet');
                    else navigate('/devices');
                  }}
                  onCompanyClick={(c) => {
                    if (c?.id != null) navigate(`/devices?company=${c.id}`);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Carousel Indicators - only show if more than 2 companies */}
          {companies.length > 2 && (
            <div className="flex justify-center gap-2 mt-3">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCompanyCarouselIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${companyCarouselIndex === idx
                    ? 'bg-cyan-500 w-6'
                    : 'bg-gray-400 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-500'
                    }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Rresht 2 (vetëm web): 2 grafikë + hapësirë – lidhur me faqen e karuselit */}
        <div className="grid grid-cols-3 gap-3 min-h-[160px] mb-4">
          {displayCompanies.map((company, chartIndex) => {
            const companyKey = String(company.id);
            const chartPeriod = timePeriodByCompanyId[companyKey] ?? timePeriodByCompanyId[company.id] ?? 'day';
            const solarData = getChartData('solar', chartPeriod, companyKey);
            // Dita: shfaqim në MW (kWh/h → MW); week/month/year mbeten në kWh për shkallë
            const displayValues = chartPeriod === 'day' ? solarData.map((v) => v / 1000) : solarData;
            const maxVal = Math.max(...displayValues, 0.001);
            const labels = getChartLabels(chartPeriod);
            const theme = desktopChartThemes[desktopChartThemesByIndex[chartIndex] ?? chartIndex];
            const pad = 4;
            const w = 200;
            const h = 120;
            const points = displayValues.map((v, i) => {
              const x = pad + (i / (displayValues.length - 1 || 1)) * (w - 2 * pad);
              const y = h - pad - (v / maxVal) * (h - 2 * pad);
              return { x, y, v };
            });
            const barW = Math.max(2, ((w - 2 * pad) / displayValues.length) * 0.75);
            const valueUnit = chartPeriod === 'day' ? 'MW' : 'MWh';
            const formatTooltipVal = (val) => (chartPeriod === 'day' ? `${Number(val).toFixed(2)} MW` : `${(Number(val) / 1000).toFixed(2)} MWh`);
            return (
              <div
                key={company.id}
                className="rounded-2xl p-3 border flex flex-col bg-white border-gray-200 shadow-sm dark:bg-[#1e293b] dark:border-[#334155] select-none h-full min-h-[160px]"
                onPointerDown={() => {
                  const idx = chartIndex;
                  desktopChartLongPressHandledRef.current = false;
                  desktopChartLongPressTimerRef.current = setTimeout(() => {
                    setDesktopChartThemesByIndex(prev => {
                      const next = [...prev];
                      next[idx] = (next[idx] + 1) % 8;
                      return next;
                    });
                    desktopChartLongPressHandledRef.current = true;
                    desktopChartLongPressTimerRef.current = null;
                  }, 3000);
                }}
                onPointerUp={() => {
                  if (desktopChartLongPressTimerRef.current) {
                    clearTimeout(desktopChartLongPressTimerRef.current);
                    desktopChartLongPressTimerRef.current = null;
                  }
                }}
                onPointerLeave={() => {
                  if (desktopChartLongPressTimerRef.current) {
                    clearTimeout(desktopChartLongPressTimerRef.current);
                    desktopChartLongPressTimerRef.current = null;
                  }
                }}
              >
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <h3 className="text-xs font-bold text-gray-800 dark:text-white truncate">{company.name}</h3>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[9px] text-gray-500 dark:text-gray-400">{valueUnit}</span>
                    <select
                      value={chartPeriod}
                      onChange={(e) => setTimePeriodByCompanyId(prev => ({ ...prev, [String(company.id)]: e.target.value }))}
                      className="text-[10px] rounded-lg px-2 py-1 bg-gray-100 dark:bg-[#0f172a] text-gray-700 dark:text-gray-300 border-0 focus:ring-1 ring-cyan-500"
                    >
                      <option value="day">Dita</option>
                      <option value="week">{t('week')}</option>
                      <option value="month">{t('month')}</option>
                      <option value="year">{t('year')}</option>
                    </select>
                  </div>
                </div>
                <div className="flex-1 min-h-0 flex flex-col items-center">
                  <svg viewBox={`0 0 ${w} ${h}`} className="w-full flex-1 min-h-[100px]" preserveAspectRatio="none">
                    {points.map((p, i) => (
                      <rect
                        key={i}
                        x={p.x - barW / 2}
                        y={p.y}
                        width={barW}
                        height={h - pad - p.y}
                        fill={theme.fill}
                        stroke={theme.stroke}
                        strokeWidth="0.5"
                        rx="1"
                        title={formatTooltipVal(p.v)}
                      />
                    ))}
                  </svg>
                  <div className="flex justify-between w-full mt-1 gap-1">
                    {(() => {
                      const tickLabels = chartPeriod === 'day'
                        ? ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:00']
                        : labels.filter((_, i) => i % Math.max(1, Math.floor(labels.length / 6)) === 0).slice(0, 6);
                      return tickLabels.map((l, i) => (
                        <span key={i} className="text-[8px] text-gray-500 dark:text-gray-400 truncate">{l}</span>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
          {/* Weather Station për diell – vetëm web; të dhëna nga /api/weather/hourly (weather_readings) */}
          {(() => {
            const firstCompany = displayCompanies[0];
            const apiWeather = firstCompany?.id ? weatherByCompanyId[firstCompany.id] : null;
            const weather = {
              irradiance: apiWeather?.irradiance ?? null,
              tempAmbient: apiWeather?.temp_ambient ?? null,
              tempModule: apiWeather?.temp_module ?? null,
              windSpeed: apiWeather?.wind_speed ?? null,
              humidity: apiWeather?.humidity ?? null,
              poa: apiWeather?.poa ?? null,
            };
            const cellClass = 'flex items-center gap-2 rounded-lg bg-gray-50 dark:bg-[#0f172a] px-2 py-1.5 border border-gray-200 dark:border-[#334155]';
            const iconClass = 'w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0';
            const valueClass = 'text-[11px] font-bold text-gray-800 dark:text-white tabular-nums';
            const labelClass = 'text-[9px] text-gray-500 dark:text-gray-400';
            return (
              <div className="rounded-2xl p-3 border flex flex-col bg-white border-gray-200 shadow-sm dark:bg-[#1e293b] dark:border-[#334155] h-full min-h-[160px]">
                <h3 className="text-xs font-bold text-gray-800 dark:text-white mb-2 flex-shrink-0">Stacioni i Motit (Solar)</h3>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 flex-1 min-h-0">
                  <div className={cellClass}>
                    <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    <div className="min-w-0">
                      <p className={labelClass}>Rrezatimi (GHI)</p>
                      <p className={valueClass}>{Number.isFinite(Number(weather.irradiance)) ? `${Number(weather.irradiance)} W/m²` : '—'}</p>
                    </div>
                  </div>
                  <div className={cellClass}>
                    <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                    <div className="min-w-0">
                      <p className={labelClass}>Temp. ambient</p>
                      <p className={valueClass}>{Number.isFinite(Number(weather.tempAmbient)) ? `${Number(weather.tempAmbient)} °C` : '—'}</p>
                    </div>
                  </div>
                  <div className={cellClass}>
                    <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" /></svg>
                    <div className="min-w-0">
                      <p className={labelClass}>Temp. modul</p>
                      <p className={valueClass}>{Number.isFinite(Number(weather.tempModule)) ? `${Number(weather.tempModule)} °C` : '—'}</p>
                    </div>
                  </div>
                  <div className={cellClass}>
                    <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                    <div className="min-w-0">
                      <p className={labelClass}>Era</p>
                      <p className={valueClass}>{Number.isFinite(Number(weather.windSpeed)) ? `${Number(weather.windSpeed)} m/s` : '—'}</p>
                    </div>
                  </div>
                  <div className={`${cellClass} col-span-2`}>
                    <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>
                    <div className="min-w-0 flex-1">
                      <p className={labelClass}>Lagështia · POA (rrafshi i paneleve)</p>
                      <p className={valueClass}>
                        {Number.isFinite(Number(weather.humidity)) ? `${Number(weather.humidity)}%` : '—'}
                        {' · '}
                        {Number.isFinite(Number(weather.poa)) ? `${Number(weather.poa)} W/m²` : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Rresht 3 (vetëm web): Impakti + 3 karta alarme – gjatësi pak më e madhe, tri kartat me gjerësi të barabartë me të parën, zënë hapësirën */}
        <div className="grid grid-cols-4 gap-3 flex-shrink-0 items-stretch min-h-[150px]">
          {/* Impakti Mjedisor – kolona 1: gjatësi pak më e madhe, gjerësi e njëjtë si kartat e tjera */}
          <div className="rounded-2xl p-3 border flex flex-col gap-2 bg-white border-gray-200 shadow-sm dark:bg-[#1e293b] dark:border-[#334155] min-w-0 min-h-[150px]">
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl px-3 py-2 flex items-center justify-center bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155]">
                <span className="text-sm font-bold text-gray-800 dark:text-white whitespace-nowrap">Impakti Mjedisor</span>
              </div>
              <div className="rounded-full px-3 py-1.5 flex items-center gap-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155]">
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /></svg>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">CO₂ Sot</span>
                <span className="text-sm font-bold text-gray-800 dark:text-white">{totalCo2Kg >= 1000 ? `${(totalCo2Kg / 1000).toFixed(1)} ton` : `${totalCo2Kg.toFixed(0)} kg`}</span>
              </div>
              <div className="rounded-full px-3 py-1.5 flex items-center gap-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155]">
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Ekuivalent Pemësh</span>
                <span className="text-sm font-bold text-gray-800 dark:text-white">{treeEquiv >= 1000 ? `${(treeEquiv / 1000).toFixed(0)}K pemë` : `${treeEquiv} pemë`}</span>
              </div>
              <div className="rounded-full px-3 py-1.5 flex items-center gap-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155]">
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Total Production</span>
                <span className="text-sm font-bold text-gray-800 dark:text-white">{totalMWh.toFixed(2)} MWh</span>
              </div>
            </div>
          </div>
          {/* Alarmet Data Logger – max 5 sot */}
          <div className="rounded-xl border bg-white border-gray-200 shadow-sm dark:bg-[#1e293b] dark:border-[#334155] overflow-hidden flex min-h-0 min-w-0">
            <div className="w-6 flex-shrink-0 border-r border-cyan-400 dark:border-cyan-600 bg-cyan-500 dark:bg-cyan-600 cursor-default" title="Alarmet Data Logger" />
            <div className="flex-1 min-h-0 min-w-0 overflow-auto overflow-x-hidden">
              <div className="px-1.5 py-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-[#334155]">Data Logger</div>
              <table className="w-full text-[10px]">
                <tbody>
                  {dashboardAlarms.datalogger.slice(0, 3).map((a, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-[#1e293b]' : 'bg-gray-50 dark:bg-[#0f172a]/50'}>
                      <td className="px-1 py-0.5 text-gray-600 dark:text-gray-400 whitespace-nowrap align-top">{formatAlarmTs(a.ts)}</td>
                      <td className="px-1 py-0.5 text-gray-700 dark:text-gray-300 truncate max-w-[90px]" title={a.company_name}>{a.company_name ?? '—'}</td>
                      <td className="px-1 py-0.5 text-red-600 dark:text-red-400 font-medium">{a.state ?? 'ON'}</td>
                    </tr>
                  ))}
                  {dashboardAlarms.datalogger.length === 0 && (
                    <tr><td colSpan={3} className="px-1 py-1 text-center text-gray-500 dark:text-gray-400 text-[9px]">—</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Alarmet Energy Meter – max 5 sot */}
          <div className="rounded-xl border bg-white border-gray-200 shadow-sm dark:bg-[#1e293b] dark:border-[#334155] overflow-hidden flex min-h-0 min-w-0">
            <div className="w-6 flex-shrink-0 border-r border-violet-400 dark:border-violet-600 bg-violet-500 dark:bg-violet-600 cursor-default" title="Alarmet Energy Meter" />
            <div className="flex-1 min-h-0 min-w-0 overflow-auto overflow-x-hidden">
              <div className="px-1.5 py-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-[#334155]">Energy Meter</div>
              <table className="w-full text-[10px]">
                <tbody>
                  {dashboardAlarms.energy_meter.slice(0, 3).map((a, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-[#1e293b]' : 'bg-gray-50 dark:bg-[#0f172a]/50'}>
                      <td className="px-1 py-0.5 text-gray-600 dark:text-gray-400 whitespace-nowrap align-top">{formatAlarmTs(a.ts)}</td>
                      <td className="px-1 py-0.5 text-gray-700 dark:text-gray-300 truncate max-w-[90px]" title={a.company_name}>{a.company_name ?? '—'}</td>
                      <td className="px-1 py-0.5 text-red-600 dark:text-red-400 font-medium">{a.state ?? 'ON'}</td>
                    </tr>
                  ))}
                  {dashboardAlarms.energy_meter.length === 0 && (
                    <tr><td colSpan={3} className="px-1 py-1 text-center text-gray-500 dark:text-gray-400 text-[9px]">—</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Alarmet Inverter – max 5 sot */}
          <div className="rounded-xl border bg-white border-gray-200 shadow-sm dark:bg-[#1e293b] dark:border-[#334155] overflow-hidden flex min-h-0 min-w-0">
            <div className="w-6 flex-shrink-0 border-r border-amber-400 dark:border-amber-600 bg-amber-500 dark:bg-amber-600 cursor-default" title="Alarmet Inverter" />
            <div className="flex-1 min-h-0 min-w-0 overflow-auto overflow-x-hidden">
              <div className="px-1.5 py-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-[#334155]">Inverter</div>
              <table className="w-full text-[10px]">
                <tbody>
                  {dashboardAlarms.inverter.slice(0, 3).map((a, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-[#1e293b]' : 'bg-gray-50 dark:bg-[#0f172a]/50'}>
                      <td className="px-1 py-0.5 text-gray-600 dark:text-gray-400 whitespace-nowrap align-top">{formatAlarmTs(a.ts)}</td>
                      <td className="px-1 py-0.5 text-gray-700 dark:text-gray-300 truncate max-w-[90px]" title={a.company_name}>{a.company_name ?? '—'}</td>
                      <td className="px-1 py-0.5 text-red-600 dark:text-red-400 font-medium">{a.state ?? 'ON'}</td>
                    </tr>
                  ))}
                  {dashboardAlarms.inverter.length === 0 && (
                    <tr><td colSpan={3} className="px-1 py-1 text-center text-gray-500 dark:text-gray-400 text-[9px]">—</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-full"><p className="text-gray-500">Duke ngarkuar...</p></div></Layout>;
  }

  return (
    <Layout>
      {/* Mobile: lartësi viewport pa scroll — përballë header + bottom nav të Layout */}
      <div className="md:hidden flex h-[calc(100dvh-6.75rem)] max-h-[calc(100dvh-6.75rem)] min-h-0 flex-col overflow-hidden">
        {mobileView === 'companies' && <MobileCompaniesView />}
        {mobileView === 'production' && <MobileProductionView />}
      </div>

      {/* Desktop */}
      <div className="hidden md:block h-full">
        <DesktopView />
      </div>

      {/* Photo Upload Modal - mobile: në mes, X */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">Ngarko Logo</h3>
              <button type="button" onClick={() => setShowPhotoModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label={t('close')}>✕</button>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            <div className="flex gap-3">
              <button onClick={() => setShowPhotoModal(false)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
                Kthehu
              </button>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                {uploading ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
